import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Upload, Sparkles, Loader, Wand2, Save, Image as ImageIcon, Film, ExternalLink, Star } from 'lucide-react';
import clsx from 'clsx';
import { ProductService, Product } from '../services/productService';
import apiClient from '../services/api/client';
import BlackFridayPriceHint, { temMercadoLivre } from './BlackFridayPriceHint';

// IA pode levar mais que o timeout padrão de 30 s da API (descrições longas)
const AI_REQ = { timeout: 90000 };

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'https://qflow.xflow.ca:5005';
const resolveMediaUrl = (url: string): string => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  if (url.startsWith('/site/api/hub/products/media')) return `${API_BASE}${url}`;
  return url;
};

interface AddProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  product?: Product & { imagesConfig?: Array<{ url: string; allowedMarketplaces: string[] }> };
  /** true = duplicar: abre com os dados do produto, mas salva como produto NOVO */
  isCopy?: boolean;
  /** Produto novo já preenchido (ex.: vindo do cadastro via chat) */
  rascunho?: Record<string, any> | null;
  rascunhoMarketplaces?: string[];
}

// Campos que identificam o produto/anúncio original e NÃO podem ir para a cópia
// (senão a cópia atualizaria o anúncio do original nos marketplaces)
const CAMPOS_NAO_COPIAR = new Set([
  '_id', 'id', 'channels', 'createdAt', 'updatedAt', 'created_at', 'updated_at', 'syncStatus', 'lastSync',
  'externalId', 'ml_item_id', 'ml_variations', 'shopee_item_id', 'shopee_model_id', 'magalu_sku_id',
  'amazon_asin', 'tiktok_product_id', 'ean', 'gtin', 'magalu_ean', 'casas_ean', 'carrefour_ean', 'leroy_ean',
]);
const _ehIdDeAnuncio = (k: string) => CAMPOS_NAO_COPIAR.has(k) || /item_?id$|external_?id$|listing_?id$|_asin$/i.test(k);

// Agrupados por categoria para organizar a interface
const MARKETPLACES = [
  // Os Gigantes de Volume
  { id: 'ml',     name: 'Mercado Livre',   category: 'Gigantes',           color: '#FFE600' },
  { id: 'shopee', name: 'Shopee',          category: 'Gigantes',           color: '#EE4D2D' },
  { id: 'amazon', name: 'Amazon',          category: 'Gigantes',           color: '#FF9900' },
  { id: 'magalu', name: 'Magazine Luiza',  category: 'Gigantes',           color: '#0086FF' },
  { id: 'shein',  name: 'Shein',           category: 'Gigantes',           color: '#222222' },
  { id: 'tiktok', name: 'TikTok Shop',    category: 'Gigantes',           color: '#010101' },

  // Grupo Casas Bahia
  { id: 'casasbahia', name: 'Casas Bahia', category: 'Grupo Casas Bahia', color: '#0057A8' },
  { id: 'pontofrio',  name: 'Ponto Frio', category: 'Grupo Casas Bahia', color: '#0070CC' },
  { id: 'extra',      name: 'Extra',      category: 'Grupo Casas Bahia', color: '#E30613' },

  // Casa e Construção
  { id: 'leroy',    name: 'Leroy Merlin', category: 'Casa e Construção', color: '#78BE20' },
  { id: 'carrefour', name: 'Carrefour',  category: 'Supermercados',      color: '#005AA9' },

  // Moda e Esporte
  { id: 'netshoes', name: 'Netshoes', category: 'Moda e Esporte', color: '#5A2D82' },
  { id: 'dafiti',   name: 'Dafiti',   category: 'Moda e Esporte', color: '#000000' },
];

// ── Ficha técnica do Mercado Livre ────────────────────────────────────────────
// Mostra os atributos da categoria escolhida. Valores do lojista ficam em
// productData.attributes com mlId; o que o backend detectou no título aparece como
// "detectado" (não é chute: só quando um único valor da lista aparece no texto).
type MlAttrValor = { id: string; name: string };
type MlFichaAttr = { id: string; name: string; obrigatorio: boolean; filtro: boolean; tipo: string; valores: MlAttrValor[]; dica?: string };
type MlFicha = { category_id: string; nome: string; caminho: string; folha: boolean; atributos: MlFichaAttr[] };
type MlPreench = { atributos: { id: string; value_name: string; value_id?: string }[]; origem: Record<string, string>; faltam_obrigatorios: string[]; faltam_filtros: string[]; completude: number };
type ProdAttr = { name: string; value: string; required: boolean; mlId?: string };

/** "Bebês > Roupas de Bebê > Vestidos" a partir do ID da categoria do ML (com cache) */
const _mlCaminhoCache: Record<string, string> = {};
async function mlCaminhoCategoria(categoryId: string): Promise<string> {
  const id = (categoryId || '').trim().toUpperCase();
  if (!id) return '';
  if (_mlCaminhoCache[id] !== undefined) return _mlCaminhoCache[id];
  try {
    const r = await fetch(`https://api.mercadolibre.com/categories/${id}`);
    if (!r.ok) return '';
    const c = await r.json();
    const caminho = (c.path_from_root || []).map((p: any) => p.name).join(' > ') || c.name || '';
    _mlCaminhoCache[id] = caminho;
    return caminho;
  } catch {
    return '';
  }
}

/** EAN-8 / UPC-12 / EAN-13 / GTIN-14 com dígito verificador correto */
export function eanValido(codigo: string): boolean {
  const d = (codigo || '').replace(/\D/g, '');
  if (![8, 12, 13, 14].includes(d.length) || /^0+$/.test(d)) return false;
  const corpo = d.slice(0, -1).split('').reverse();
  const soma = corpo.reduce((s, n, i) => s + Number(n) * (i % 2 === 0 ? 3 : 1), 0);
  return (10 - (soma % 10)) % 10 === Number(d[d.length - 1]);
}

const ML_ESPECIAIS = new Set(['BRAND', 'GTIN', 'SELLER_SKU', 'SIZE_GRID_ID', 'SIZE_GRID_ROW_ID', 'EMPTY_GTIN_REASON', 'ITEM_CONDITION', 'PRODUCT_DATA_SOURCE']);
const normTxt = (t: string) => (t || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

function MlFichaTecnica({ categoryId, produto, attributes, onChange, onFaltam, onUsarCategoria }: {
  categoryId: string;
  produto: { title: string; description: string; brand: string; size?: string; category?: string };
  attributes: ProdAttr[];
  onChange: (attrs: ProdAttr[]) => void;
  onFaltam: (faltam: string[] | null) => void;
  onUsarCategoria?: (id: string) => void;
}) {
  const [ficha, setFicha] = useState<MlFicha | null>(null);
  // Categoria geral → subcategoria final que a publicação vai usar (a ficha mostrada é a dela)
  const [catFinal, setCatFinal] = useState<{ de: string; id: string; caminho: string } | null>(null);
  const [preench, setPreench] = useState<MlPreench | null>(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');
  const [mostrarTodos, setMostrarTodos] = useState(false);

  const valido = /^MLB\d+$/i.test(categoryId || '');

  useEffect(() => {
    if (!valido) { setFicha(null); setPreench(null); onFaltam(null); return; }
    const t = setTimeout(async () => {
      setLoading(true); setErro('');
      try {
        const r = await apiClient.post(`/ml/categories/${categoryId.toUpperCase()}/ficha`, { ...produto, attributes });
        setFicha(r.data.ficha); setPreench(r.data.preenchimento);
        setCatFinal(r.data.categoriaFinal || null);
      } catch (e: any) {
        setErro(e?.response?.data?.error || 'Não foi possível carregar a ficha técnica do Mercado Livre.');
        setFicha(null); setPreench(null); setCatFinal(null);
      } finally { setLoading(false); }
    }, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId, produto.title, produto.description, produto.brand, produto.size]);

  const valorLojista = (a: MlFichaAttr) =>
    attributes.find(x => (x.mlId || '').toUpperCase() === a.id)?.value
    ?? attributes.find(x => !x.mlId && normTxt(x.name) === normTxt(a.name))?.value ?? '';
  const detectado = (a: MlFichaAttr) =>
    preench?.origem?.[a.id] === 'texto' ? preench.atributos.find(x => x.id === a.id)?.value_name || '' : '';

  const visiveis = (ficha?.atributos || []).filter(a => !ML_ESPECIAIS.has(a.id));
  const faltando = visiveis.filter(a => !valorLojista(a) && !detectado(a));
  const faltamObrig = faltando.filter(a => a.obrigatorio).map(a => a.name);
  const completude = visiveis.length ? Math.round(100 * (visiveis.length - faltando.length) / visiveis.length) : 100;

  useEffect(() => { onFaltam(ficha ? faltamObrig : null); /* eslint-disable-next-line */ }, [ficha, faltamObrig.join('|')]);

  const setValor = (a: MlFichaAttr, valor: string) => {
    const resto = attributes.filter(x => !((x.mlId || '').toUpperCase() === a.id || (!x.mlId && normTxt(x.name) === normTxt(a.name))));
    onChange(valor ? [...resto, { name: a.name, value: valor, required: a.obrigatorio, mlId: a.id }] : resto);
  };

  if (!valido) return null;
  if (loading && !ficha) return <p className="text-xs text-dark-400 mt-3">Carregando ficha técnica da categoria…</p>;
  if (erro) return <p className="text-xs text-red-700 mt-3">{erro}</p>;
  if (!ficha) return null;

  const lista = mostrarTodos ? visiveis : visiveis.filter(a => a.obrigatorio || !valorLojista(a) && !detectado(a) || valorLojista(a));
  const cor = completude === 100 ? 'text-emerald-700' : faltamObrig.length ? 'text-red-700' : 'text-amber-700';

  return (
    <div className="col-span-2 mt-2 p-4 rounded-lg border border-dark-700 bg-dark-900/60">
      <div className="flex items-start justify-between gap-3 mb-1">
        <div>
          <h4 className="text-dark-100 font-semibold">Ficha técnica do Mercado Livre</h4>
          <p className="text-xs text-dark-500">{ficha.caminho}{!ficha.folha && ' — categoria geral: o QFlow escolhe a subcategoria final ao publicar'}</p>
        </div>
        <span className={`text-sm font-bold ${cor}`}>{completude}%</span>
      </div>
      {catFinal && (
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3 p-2.5 rounded border border-amber-300 bg-amber-50 text-xs text-amber-900">
          <span>
            A categoria <strong>{catFinal.de}</strong> é geral. O anúncio será publicado em <strong>{catFinal.caminho}</strong> ({catFinal.id}) — esta é a ficha dela.
          </span>
          {onUsarCategoria && (
            <button type="button" onClick={() => onUsarCategoria(catFinal.id)}
                    className="px-2.5 py-1 rounded bg-amber-600 hover:bg-amber-700 text-white font-semibold">
              Usar {catFinal.id}
            </button>
          )}
        </div>
      )}
      {faltando.length > 0 ? (
        <p className={`text-xs mb-3 ${faltamObrig.length ? 'text-red-700' : 'text-amber-700'}`}>
          {faltamObrig.length > 0 && <>Obrigatórios faltando: <strong>{faltamObrig.join(', ')}</strong>. </>}
          {faltando.filter(a => !a.obrigatorio).length > 0 && <>Para aparecer nos filtros de busca, preencha também: {faltando.filter(a => !a.obrigatorio).map(a => a.name).join(', ')}.</>}
        </p>
      ) : <p className="text-xs text-emerald-700 mb-3">Ficha completa.</p>}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {lista.map(a => {
          const v = valorLojista(a);
          const det = !v ? detectado(a) : '';
          const vazio = !v && !det;
          return (
            <div key={a.id}>
              <label className="flex items-center gap-2 text-xs font-medium text-dark-300 mb-1">
                {a.name}{a.obrigatorio && <span className="text-red-700">*</span>}
                {a.filtro && !a.obrigatorio && <span className="px-1.5 rounded bg-dark-700 text-dark-400">filtro</span>}
                {det && <span className="px-1.5 rounded bg-emerald-50 text-emerald-700" title="Encontrado no título/descrição">detectado</span>}
              </label>
              {a.valores.length > 0 ? (
                <select value={v || det} onChange={e => setValor(a, e.target.value)}
                        className={clsx('w-full px-3 py-2 bg-dark-900 border rounded-lg text-dark-100 text-sm',
                          vazio && a.obrigatorio ? 'border-red-400' : 'border-dark-700')}>
                  <option value="">— selecione —</option>
                  {a.valores.map(o => <option key={o.id} value={o.name}>{o.name}</option>)}
                </select>
              ) : (
                <input type="text" value={v} placeholder={det || a.dica || ''} onChange={e => setValor(a, e.target.value)}
                       className={clsx('w-full px-3 py-2 bg-dark-900 border rounded-lg text-dark-100 text-sm',
                         vazio && a.obrigatorio ? 'border-red-400' : 'border-dark-700')} />
              )}
            </div>
          );
        })}
      </div>
      {visiveis.length > lista.length && (
        <button type="button" onClick={() => setMostrarTodos(true)} className="mt-3 text-xs text-primary-400 hover:underline">
          Mostrar todos os {visiveis.length} atributos
        </button>
      )}
    </div>
  );
}

// ── Tabela de medidas da Shopee ───────────────────────────────────────────────
// Categorias de moda exigem tabela de medidas. O lojista escolhe uma tabela já
// criada na loja (Seller Centre → Tabela de medidas) ou envia a imagem da tabela.
function ShopeeTabelaMedidas({ categoryId, tabelaId, imagem, onChange }: {
  categoryId: string;
  tabelaId: string;
  imagem: string;
  onChange: (campos: { shopee_size_chart_id?: string; shopee_size_chart_image?: string }) => void;
}) {
  const [tabelas, setTabelas] = useState<{ id: number; nome: string }[]>([]);
  const [exige, setExige] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [modo, setModo] = useState<'tabela' | 'imagem'>(imagem && !tabelaId ? 'imagem' : 'tabela');

  useEffect(() => {
    if (!/^\d{5,}$/.test(categoryId || '')) { setTabelas([]); setExige(null); return; }
    let ativo = true;
    setLoading(true); setErro('');
    apiClient.get('/shopee/size-charts', { params: { category_id: categoryId } })
      .then(r => { if (ativo) { setTabelas(r.data.tabelas || []); setExige(r.data.exige ?? null); setErro(r.data.aviso || ''); } })
      .catch(e => {
        if (!ativo) return;
        const st = e?.response?.status;
        setErro(e?.response?.data?.error
          || (st === 404 ? 'O servidor ainda não tem a busca de tabelas de medidas (atualize a API do QFlow).'
                         : 'Não foi possível carregar as tabelas de medidas da Shopee.')
          + ' Você pode usar "Enviar imagem da tabela".');
      })
      .finally(() => { if (ativo) setLoading(false); });
    return () => { ativo = false; };
  }, [categoryId]);

  const enviarImagem = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setEnviando(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('userCode', localStorage.getItem('userCode') || '');
      const r = await apiClient.post('/products/upload-media', fd);
      onChange({ shopee_size_chart_image: r.data.url, shopee_size_chart_id: '' });
    } catch (err: any) {
      alert(`Erro ao enviar a imagem: ${err?.response?.data?.error || err?.message}`);
    } finally { setEnviando(false); e.target.value = ''; }
  };

  if (!categoryId) return null;
  const preenchido = !!(tabelaId || imagem);

  return (
    <div className="col-span-3">
      <label className="text-sm font-medium text-dark-300 mb-2 flex items-center gap-2">
        Tabela de medidas (Shopee)
        {exige && <span className="text-red-400">* obrigatória nesta categoria</span>}
        {exige === false && <span className="text-xs text-dark-500">não usada nesta categoria</span>}
        {loading && <span className="text-xs text-dark-400">carregando…</span>}
      </label>

      <div className="flex gap-2 mb-2 text-xs">
        <button type="button" onClick={() => setModo('tabela')}
                className={clsx('px-3 py-1 rounded border', modo === 'tabela' ? 'border-primary-500 text-primary-400' : 'border-dark-700 text-dark-400')}>
          Tabela da loja
        </button>
        <button type="button" onClick={() => setModo('imagem')}
                className={clsx('px-3 py-1 rounded border', modo === 'imagem' ? 'border-primary-500 text-primary-400' : 'border-dark-700 text-dark-400')}>
          Enviar imagem da tabela
        </button>
      </div>

      {modo === 'tabela' ? (
        <>
          <select value={tabelaId} onChange={e => onChange({ shopee_size_chart_id: e.target.value, shopee_size_chart_image: '' })}
                  className={clsx('w-full px-4 py-3 bg-dark-900 border rounded-lg text-dark-100',
                    exige && !preenchido ? 'border-red-500/50' : 'border-dark-700')}>
            <option value="">— selecione —</option>
            {tabelas.map(t => <option key={t.id} value={String(t.id)}>{t.nome}</option>)}
            {tabelaId && !tabelas.some(t => String(t.id) === tabelaId) && <option value={tabelaId}>Tabela {tabelaId}</option>}
          </select>
          {!loading && tabelas.length === 0 && !erro && (
            <p className="text-xs text-dark-500 mt-1">
              Nenhuma tabela de medidas criada na sua loja para esta categoria. Crie no Seller Centre da Shopee
              (Produtos → Tabela de medidas) e reabra este produto, ou use "Enviar imagem da tabela".
            </p>
          )}
        </>
      ) : (
        <div className="flex items-center gap-3">
          {imagem && <img src={imagem.startsWith('http') ? imagem : `${API_BASE}${imagem}`} alt="Tabela de medidas" className="h-16 rounded border border-dark-700" />}
          <label className="px-3 py-2 rounded-lg bg-dark-800 border border-dark-700 text-sm text-dark-200 cursor-pointer hover:bg-dark-700">
            {enviando ? 'Enviando…' : imagem ? 'Trocar imagem' : 'Escolher imagem'}
            <input type="file" accept="image/*" className="hidden" onChange={enviarImagem} disabled={enviando} />
          </label>
          {imagem && <button type="button" className="text-xs text-red-400 hover:underline" onClick={() => onChange({ shopee_size_chart_image: '' })}>remover</button>}
        </div>
      )}
      {erro && <p className="text-xs text-red-400 mt-1">{erro}</p>}
    </div>
  );
}

// ── CategoryAutocomplete ──────────────────────────────────────────────────────
// Componente reutilizável de autocomplete de categoria por marketplace.
// Chama /marketplace-categories?marketplace=xxx&q=yyy com debounce.
// Se nenhuma sugestão for retornada, permite digitação livre.
type CatSugestao = { name: string; category_id: string; caminho?: string; folha?: boolean };

function CategoryAutocomplete({
  marketplace, value, onChange, onPick, placeholder, className,
}: {
  marketplace: string;
  value: string;
  onChange: (val: string) => void;
  onPick?: (id: string) => void;
  placeholder?: string;
  className?: string;
}) {
  const [inputText, setInputText] = React.useState(value);
  const [suggestions, setSuggestions] = React.useState<CatSugestao[]>([]);
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [escolhida, setEscolhida] = React.useState<CatSugestao | null>(null);
  const timer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(() => { setInputText(value); }, [value]);

  // Produto já cadastrado: mostra o caminho da categoria salva (ID)
  React.useEffect(() => {
    if (!value || !/^\d+$/.test(value) || escolhida?.category_id === value) return;
    apiClient.get('/marketplace-categories/search', { params: { marketplace, q: value } })
      .then(r => setEscolhida((Array.isArray(r.data) ? r.data : []).find((c: CatSugestao) => c.category_id === value) || null))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, marketplace]);

  const handleInput = (q: string) => {
    setInputText(q);
    setEscolhida(null);
    onChange(q);
    if (timer.current) clearTimeout(timer.current);
    if (q.trim().length < 2) { setSuggestions([]); setOpen(false); return; }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await apiClient.get('/marketplace-categories/search', { params: { marketplace, q } });
        const data: CatSugestao[] = Array.isArray(r.data) ? r.data : [];
        setSuggestions(data.slice(0, 15));
        setOpen(true);
      } catch {
        setSuggestions([]); setOpen(false);
      } finally {
        setLoading(false);
      }
    }, 300);
  };

  const pick = (item: CatSugestao) => {
    setInputText(item.category_id);
    setEscolhida(item);
    onChange(item.category_id);
    onPick?.(item.category_id);
    setSuggestions([]);
    setOpen(false);
  };

  const baseClass = className ?? 'w-full px-4 py-3 bg-dark-900 border border-dark-700 rounded-lg text-dark-100';

  return (
    <div className="relative">
      <input
        type="text"
        value={inputText}
        onChange={e => handleInput(e.target.value)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        onFocus={() => { if (suggestions.length > 0) setOpen(true); }}
        placeholder={placeholder}
        className={baseClass}
      />
      {loading && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-dark-400 pointer-events-none">
          buscando…
        </span>
      )}
      {open && (
        <ul className="absolute z-50 w-full mt-1 bg-dark-800 border border-dark-600 rounded-lg shadow-xl max-h-72 overflow-auto">
          {suggestions.length === 0 && !loading && (
            <li className="px-4 py-2.5 text-sm text-dark-400">Nenhuma categoria encontrada. Tente outra palavra.</li>
          )}
          {suggestions.map(s => (
            <li
              key={s.category_id}
              onMouseDown={() => pick(s)}
              className="px-4 py-2.5 cursor-pointer hover:bg-dark-700 border-b border-dark-700 last:border-0"
            >
              <p className="text-dark-100 text-sm">{s.caminho || s.name}</p>
              <p className="text-dark-500 text-xs">
                {s.category_id}
                {s.folha === false && <span className="ml-2 text-yellow-400">categoria geral — escolha uma mais específica</span>}
              </p>
            </li>
          ))}
        </ul>
      )}
      {escolhida && (
        <p className={clsx('text-xs mt-1', escolhida.folha === false ? 'text-yellow-400' : 'text-emerald-400')}>
          Categoria: {escolhida.caminho || escolhida.name}
          {escolhida.folha === false && ' — categoria geral; o QFlow usa uma subcategoria final ao publicar'}
        </p>
      )}
    </div>
  );
}

// ── TikTokCategoryAutocomplete ────────────────────────────────────────────────
// Autocomplete de categoria específico para TikTok Shop.
// Carrega todas as categorias folha de /tiktok/categories na primeira abertura
// e filtra localmente. Permite digitação livre se TikTok não estiver conectado.
function TikTokCategoryAutocomplete({
  value, onChange, className,
}: {
  value: string;
  onChange: (val: string) => void;
  className?: string;
}) {
  const [inputText, setInputText] = React.useState(value);
  const [all, setAll] = React.useState<{ name: string; id: string }[]>([]);
  const [suggestions, setSuggestions] = React.useState<{ name: string; id: string }[]>([]);
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [fetchError, setFetchError] = React.useState('');

  React.useEffect(() => { setInputText(value); }, [value]);

  const loadAll = async () => {
    if (all.length > 0 || loading) return;
    setLoading(true);
    setFetchError('');
    try {
      const r = await apiClient.get('/tiktok/categories');
      const cats: { name: string; id: string }[] = r.data?.categories ?? [];
      setAll(cats);
    } catch (e: any) {
      const msg = e?.response?.data?.error || 'TikTok Shop não conectado. Conecte em Configurações.';
      setFetchError(msg);
    } finally {
      setLoading(false);
    }
  };

  const filter = (q: string, list: { name: string; id: string }[]) =>
    list.filter(c =>
      c.name.toLowerCase().includes(q.toLowerCase()) || c.id.includes(q)
    ).slice(0, 15);

  const handleInput = (q: string) => {
    setInputText(q);
    onChange(q);
    if (!q) { setSuggestions([]); setOpen(false); return; }
    const filtered = filter(q, all);
    setSuggestions(filtered);
    setOpen(filtered.length > 0);
  };

  const handleFocus = async () => {
    await loadAll();
    if (inputText) {
      const filtered = filter(inputText, all);
      setSuggestions(filtered);
      setOpen(filtered.length > 0);
    }
  };

  const pick = (item: { name: string; id: string }) => {
    setInputText(item.id);
    onChange(item.id);
    setSuggestions([]);
    setOpen(false);
  };

  const baseClass = className ?? 'w-full px-4 py-3 bg-dark-900 border border-dark-700 rounded-lg text-dark-100';

  return (
    <div className="relative">
      <input
        type="text"
        value={inputText}
        onChange={e => handleInput(e.target.value)}
        onFocus={handleFocus}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        placeholder="Digite para buscar (ex: Eletrônicos, Limpeza...)"
        className={baseClass}
      />
      {loading && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-dark-400 pointer-events-none">
          carregando…
        </span>
      )}
      {fetchError && (
        <p className="text-xs text-red-400 mt-1">{fetchError}</p>
      )}
      {open && suggestions.length > 0 && (
        <ul className="absolute z-50 w-full mt-1 bg-dark-800 border border-dark-600 rounded-lg shadow-xl max-h-48 overflow-auto">
          {suggestions.map(s => (
            <li
              key={s.id}
              onMouseDown={() => pick(s)}
              className="px-4 py-2.5 cursor-pointer hover:bg-dark-700 border-b border-dark-700 last:border-0"
            >
              <p className="text-dark-100 text-sm">{s.name}</p>
              <p className="text-dark-500 text-xs">{s.id}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}



// Regras oficiais aproximadas de resolução e peso (MB) de cada canal
const MARKETPLACE_IMAGE_RULES: Record<string, {
  minWidth: number; minHeight: number; maxSizeMB: number; name: string;
  recommendedWidth?: number; recommendedHeight?: number;
  requireSquare?: boolean; requireWhiteBackground?: boolean;
}> = {
  ml:        { minWidth: 500,  minHeight: 500,  maxSizeMB: 10, name: 'Mercado Livre',   recommendedWidth: 1200, recommendedHeight: 1200, requireSquare: true, requireWhiteBackground: true },
  magalu:    { minWidth: 800,  minHeight: 800,  maxSizeMB: 10, name: 'Magazine Luiza',  recommendedWidth: 1200, recommendedHeight: 1200, requireSquare: true, requireWhiteBackground: true },
  carrefour: { minWidth: 800,  minHeight: 800,  maxSizeMB: 10, name: 'Carrefour',       recommendedWidth: 1000, recommendedHeight: 1000 },
  leroy:     { minWidth: 1000, minHeight: 1000, maxSizeMB: 10, name: 'Leroy Merlin' },
  amazon:    { minWidth: 1000, minHeight: 1000, maxSizeMB: 10, name: 'Amazon',           recommendedWidth: 1500, recommendedHeight: 1500, requireSquare: true, requireWhiteBackground: true },
  shopee:    { minWidth: 500,  minHeight: 500,  maxSizeMB: 2,  name: 'Shopee',           recommendedWidth: 800,  recommendedHeight: 800,  requireSquare: true },
  shein:     { minWidth: 800,  minHeight: 800,  maxSizeMB: 3,  name: 'Shein' },
  netshoes:  { minWidth: 1000, minHeight: 1000, maxSizeMB: 10, name: 'Netshoes' },
  dafiti:    { minWidth: 1000, minHeight: 1200, maxSizeMB: 5,  name: 'Dafiti' },
  casasbahia: { minWidth: 800,  minHeight: 800,  maxSizeMB: 5,  name: 'Casas Bahia',    recommendedWidth: 1000, recommendedHeight: 1000 },
  pontofrio:  { minWidth: 800,  minHeight: 800,  maxSizeMB: 5,  name: 'Ponto Frio' },
  extra:      { minWidth: 800,  minHeight: 800,  maxSizeMB: 5,  name: 'Extra' },
};

export default function AddProductModal({ isOpen, onClose, onSuccess, product, isCopy = false, rascunho = null, rascunhoMarketplaces = [] }: AddProductModalProps) {
  // Editando um produto existente (na cópia o produto serve só de modelo)
  const editando = !!product && !isCopy;
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedMarketplaces, setSelectedMarketplaces] = useState<string[]>([]);
  const [stepError, setStepError] = useState('');

  // Black Friday ML: preço salvo do produto (edição) para detectar aumento
  const precoOriginal = editando && product
    ? (Number((product as any).ml_price) > 0 ? Number((product as any).ml_price) : (product.basePrice != null ? Number(product.basePrice) : null))
    : null;

  // Obrigatórios da ficha técnica do ML que ainda faltam (null = ficha não carregada)
  const [mlFaltam, setMlFaltam] = useState<string[] | null>(null);

  // ── ML Category Autocomplete ──────────────────────────────────────────────
  const [mlSuggestions, setMlSuggestions] = useState<{category_id:string; category_title:string; domain_id:string; domain_title:string; category_name?:string; domain_name?:string; caminho?:string}[]>([]);
  // Caminho completo da categoria escolhida (ex.: "Bebês > Roupas de Bebê > Vestidos")
  const [mlCaminho, setMlCaminho] = useState('');
  const [mlSearching, setMlSearching] = useState(false);
  const [mlOpen, setMlOpen] = useState(false);
  const mlTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const searchMlCategories = (q: string) => {
    if (mlTimer.current) clearTimeout(mlTimer.current);
    setProductData(p => ({...p, ml_category_id: q}));
    if (q.length < 2) { setMlSuggestions([]); setMlOpen(false); return; }
    mlTimer.current = setTimeout(async () => {
      setMlSearching(true);
      try {
        const r = await fetch(`https://api.mercadolibre.com/sites/MLB/domain_discovery/search?q=${encodeURIComponent(q)}&limit=8`);
        const data = await r.json();
        const lista = Array.isArray(data) ? data.slice(0, 8) : [];
        // O ML devolve só o nome da última categoria ("Vestidos") — busca o caminho de cada uma
        const comCaminho = await Promise.all(lista.map(async (s: any) => ({
          ...s, caminho: await mlCaminhoCategoria(s.category_id),
        })));
        setMlSuggestions(comCaminho);
        setMlOpen(true);
      } catch { setMlSuggestions([]); }
      finally { setMlSearching(false); }
    }, 400);
  };

  const pickMlSuggestion = (s: typeof mlSuggestions[0]) => {
    setProductData(p => ({...p, ml_category_id: s.category_id, ml_domain_id: s.domain_id}));
    setMlCaminho(s.caminho || '');
    setMlSuggestions([]); setMlOpen(false);
  };

  // ── Shopee Brands ─────────────────────────────────────────────────────────
  const [shopeeBrands, setShopeeBrands] = useState<{brand_id: number; display_brand_name: string}[]>([]);
  const [shopeeBrandsLoading, setShopeeBrandsLoading] = useState(false);
  const [shopeeBrandSearch, setShopeeBrandSearch] = useState('');
  const [shopeeBrandOpen, setShopeeBrandOpen] = useState(false);
  const [registeringBrand, setRegisteringBrand] = useState(false);
  const [brandRegisterMsg, setBrandRegisterMsg] = useState('');

  const fetchShopeeBrands = async (categoryId: string) => {
    if (!categoryId) return;
    setShopeeBrandsLoading(true);
    try {
      const r = await apiClient.get('/shopee/brands', { params: { category_id: categoryId } });
      setShopeeBrands(r.data.brands || []);
    } catch { setShopeeBrands([]); }
    finally { setShopeeBrandsLoading(false); }
  };

  const registerShopeeBrand = async () => {
    const brandToRegister = productData.brand.trim();
    if (!brandToRegister) return;
    setRegisteringBrand(true);
    setBrandRegisterMsg('');
    try {
      const catId = productData.shopee_category_id ? parseInt(productData.shopee_category_id) : undefined;
      await apiClient.post('/shopee/brands/register', {
        original_brand_name: brandToRegister,
        ...(catId ? { category_list: [catId] } : {}),
        ...(editando && product?._id ? { product_id: product._id } : {}),
      });
      setBrandRegisterMsg(`✅ Marca "${brandToRegister}" enviada para aprovação na Shopee.`);
    } catch (e: any) {
      setBrandRegisterMsg(`❌ ${e.message || 'Erro ao solicitar registro'}`);
    } finally { setRegisteringBrand(false); }
  };

  // ── Magalu Category Autocomplete ──────────────────────────────────────────
  // Substituído por CategoryAutocomplete (componente acima) — sem lista hardcoded.
  const [generatingTitle, setGeneratingTitle] = useState(false);
  const [generatingDescription, setGeneratingDescription] = useState(false);
  // Bloqueia todos os botões IA enquanto qualquer geração estiver em andamento
  const [aiLocked, setAiLocked] = useState(false);
  const [uploadingMap, setUploadingMap] = useState<{ [key: number]: boolean }>({});
  const [mlDiagnosticMap, setMlDiagnosticMap] = useState<{
    [index: number]: { ok: boolean; issues: { code: string; message: string }[]; warnings: { code: string; message: string }[] } | null
  }>({});

  // Categoria
  const [categories, setCategories] = useState<string[]>([]);
  const [categorySearch, setCategorySearch] = useState('');
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [generatingCategory, setGeneratingCategory] = useState(false);

  const initialProductState = {
    sku: '', title: '', description: '', category: '', brand: '', basePrice: '', stock: '',
    images: [{ url: '', allowedMarketplaces: [] as string[] }],
    weight: '', height: '', width: '', length: '',

    // Categorias por marketplace (IDs específicos de cada plataforma)
    ml_category_id: '', magalu_category_id: '', shopee_category_id: '', amazon_category_id: '',
    carrefour_category_id: '', leroy_category_id: '', netshoes_category_id: '', dafiti_category_id: '',
    tiktok_category_id: '', casasbahia_category_id: '',

    // Magalu — group agrupa variantes (obrigatório na API; se não preenchido usa o SKU)
    magalu_group: '',

    // ML Catálogo — campos obrigatórios para modo catálogo
    // domain_id: tipo de produto ML (ex: LAPTOPS, T_SHIRTS, CELL_PHONES_NEW)
    // Consulte: https://api.mercadolibre.com/product_identifiers/search?category_id=MLB1051
    ml_domain_id: '',
    // Tabela de medidas do ML (SIZE_GRID_ID) — obrigatória em categorias de moda
    ml_size_grid_id: '',
    // ml_family_name: nome da família de variantes (igual para cor/tamanho do mesmo produto)
    ml_family_name: '',

    // Gigantes
    ml_condition: 'new', ml_listing_type: 'free', ml_buying_mode: 'buy_it_now', ml_currency: 'BRL', ml_warranty: '', ml_video_id: '',
    // EAN/GTIN único do produto (vale para todos os marketplaces; copiado para os campos *_ean)
    ean: '',
    magalu_ean: '', magalu_ncm: '', magalu_origin: 'NACIONAL', magalu_warranty_time: '90', magalu_warranty_text: 'Garantia do fabricante',
    amazon_asin: '', amazon_condition: 'New',
    shopee_condition: 'NEW', shopee_pre_order: 'false', shopee_days_to_ship: '0', shopee_brand_name: '',
    americanas_crossdocking: '0', shein_material: '', aliexpress_freight_template: '',

    // Casa e Supermercado
    casas_ean: '', casas_ncm: '', casas_brand_name: '', casas_seller_sku: '', casas_model: '', casas_voltage: '',
    carrefour_ean: '', carrefour_product_tax_code: '', carrefour_eco_tax: '', carrefour_logistic_class: 'STANDARD',
    leroy_ean: '', leroy_reference: '', leroy_product_type: '', leroy_country_of_origin: 'BR',
    madeira_assembly_required: 'false', madeira_material: '', tokstok_assembly: '',

    // Moda, Desporto e Pets
    netshoes_color: '', netshoes_size: '', netshoes_gender: '',
    centauro_color: '', centauro_size: '',
    dafiti_color: '', dafiti_size: '', decathlon_model_code: '',
    pet_species: '', pet_life_stage: '', rd_ms_registry: '',

    attributes: [] as Array<{ name: string; value: string; required: boolean; mlId?: string }>,
  };

  const [productData, setProductData] = useState(initialProductState);
  const mlPrecoSubiu = temMercadoLivre(selectedMarketplaces) && precoOriginal != null && precoOriginal > 0
    && (Number((productData as any).ml_price) > 0 ? Number((productData as any).ml_price) : Number(productData.basePrice)) > precoOriginal;

  // Ao abrir um produto já cadastrado (ou digitar o ID na mão), mostra o caminho da categoria.
  // Precisa vir DEPOIS do useState de productData (antes dava "Cannot access before initialization").
  useEffect(() => {
    const id = (productData.ml_category_id || '').trim().toUpperCase();
    if (!/^MLB\d+$/.test(id)) { setMlCaminho(''); return; }
    let ativo = true;
    mlCaminhoCategoria(id).then(c => { if (ativo) setMlCaminho(c); });
    return () => { ativo = false; };
  }, [productData.ml_category_id]);

  // ── Validação por passo ───────────────────────────────────────────────────
  const validateStep = (step: number): string => {
    if (step === 1) {
      if (!productData.sku.trim())       return 'SKU / Código é obrigatório.';
      if (isCopy && productData.sku.trim().toLowerCase() === String(product?.sku || '').toLowerCase())
        return 'A cópia precisa de um SKU diferente do produto original.';
      if (!productData.title.trim())     return 'Título é obrigatório.';
      if (!productData.basePrice.trim()) return 'Preço é obrigatório.';
      if (!productData.stock.trim())     return 'Estoque é obrigatório.';
      if (productData.ean.trim() && !eanValido(productData.ean))
        return 'EAN inválido: o último dígito (verificador) não confere. Confira o código na embalagem ou com o fornecedor.';
    }
    if (step === 2) {
      if (selectedMarketplaces.length === 0) return 'Selecione ao menos um marketplace.';
      const required: { mp: string; field: keyof typeof productData; label: string }[] = [
        { mp: 'ml',         field: 'ml_category_id',       label: 'Categoria (Mercado Livre)' },
        { mp: 'shopee',     field: 'shopee_category_id',   label: 'Categoria (Shopee)' },
        { mp: 'tiktok',     field: 'tiktok_category_id',   label: 'Categoria (TikTok Shop)' },
        { mp: 'amazon',     field: 'amazon_category_id',   label: 'Categoria (Amazon)' },
        { mp: 'magalu',     field: 'magalu_ean',           label: 'EAN (Magazine Luiza)' },
        { mp: 'leroy',      field: 'magalu_ean',           label: 'EAN (Leroy Merlin)' },
        { mp: 'carrefour',  field: 'magalu_ean',           label: 'EAN (Carrefour)' },
        { mp: 'casasbahia', field: 'magalu_ean',           label: 'EAN (Casas Bahia)' },
        { mp: 'pontofrio',  field: 'magalu_ean',           label: 'EAN (Ponto Frio)' },
        { mp: 'extra',      field: 'magalu_ean',           label: 'EAN (Extra)' },
        { mp: 'netshoes',   field: 'netshoes_color',       label: 'Cor (Netshoes)' },
        { mp: 'netshoes',   field: 'netshoes_size',        label: 'Tamanho (Netshoes)' },
        { mp: 'dafiti',     field: 'dafiti_color',         label: 'Cor (Dafiti)' },
        { mp: 'dafiti',     field: 'dafiti_size',          label: 'Tamanho (Dafiti)' },
      ];
      const missing = required
        .filter(r => selectedMarketplaces.includes(r.mp) && !String(productData[r.field] ?? '').trim())
        .map(r => r.label);
      // dedupe (EAN pode aparecer várias vezes se múltiplos do Grupo CB selecionados)
      if (selectedMarketplaces.includes('ml') && mlFaltam && mlFaltam.length > 0) {
        missing.push(`Ficha técnica do Mercado Livre: ${mlFaltam.join(', ')}`);
      }
      const unique = [...new Set(missing)];
      if (unique.length > 0) return `Preencha os campos obrigatórios:\n• ${unique.join('\n• ')}`;
    }
    return '';
  };

  const handleNext = () => {
    const err = validateStep(currentStep);
    if (err) { setStepError(err); return; }
    setStepError('');
    setCurrentStep(prev => prev + 1);
  };

  useEffect(() => {
    if (isOpen) {
      setCurrentStep(1);
      setStepError('');
      if (product) {
        const loadedMarketplaces = product.channels?.map(c => c.marketplaceId) || [];

        let loadedImages = [{ url: '', allowedMarketplaces: loadedMarketplaces }];
        if (product.imagesConfig && Array.isArray(product.imagesConfig) && product.imagesConfig.length > 0) {
          loadedImages = product.imagesConfig;
        } else if (Array.isArray(product.images) && product.images.length > 0) {
          loadedImages = product.images.map(img => ({
            url: typeof img === 'string' ? img : (img as any).url || '',
            allowedMarketplaces: loadedMarketplaces
          }));
        }

        setProductData({
          ...initialProductState,
          ...product,
          basePrice: (product.basePrice !== undefined && product.basePrice !== null) ? product.basePrice.toString() : '',
          stock: (product.stock !== undefined && product.stock !== null) ? product.stock.toString() : '',
          images: loadedImages,
          weight: (product.weight !== undefined && product.weight !== null) ? product.weight.toString() : '',
          height: (product.height !== undefined && product.height !== null) ? product.height.toString() : '',
          width: (product.width !== undefined && product.width !== null) ? product.width.toString() : '',
          length: (product.length !== undefined && product.length !== null) ? product.length.toString() : '',
          attributes: Array.isArray(product.attributes) ? product.attributes : [],
          ean: (product as any).ean || (product as any).gtin || (product as any).magalu_ean || (product as any).casas_ean || '',
        });
        if (isCopy) {
          // Cópia: tira IDs do original/anúncios, EAN (é por tamanho) e estoque; sugere SKU para trocar
          setProductData(prev => {
            const limpo: any = { ...initialProductState };
            Object.entries(prev).forEach(([k, v]) => { if (!_ehIdDeAnuncio(k)) limpo[k] = v; });
            return {
              ...limpo,
              sku: '',
              stock: '',
              ean: '', magalu_ean: '', casas_ean: '', carrefour_ean: '', leroy_ean: '',
            };
          });
          // SKU sequencial: próximo número livre da loja com o mesmo prefixo (babyjoy124 → babyjoy128)
          apiClient.get('/products/next-sku', { params: { base: product.sku || '' } })
            .then(r => setProductData(prev => (prev.sku ? prev : { ...prev, sku: r.data.sku || '' })))
            .catch(() => {});
        }
        setSelectedMarketplaces(loadedMarketplaces);
      } else if (rascunho) {
        // Veio do cadastro via chat: abre preenchido, como produto novo
        setProductData({
          ...initialProductState,
          ...rascunho,
          basePrice: rascunho.basePrice != null ? String(rascunho.basePrice) : '',
          stock: rascunho.stock != null ? String(rascunho.stock) : '',
          images: Array.isArray(rascunho.images) && rascunho.images.length ? rascunho.images : initialProductState.images,
          attributes: Array.isArray(rascunho.attributes) ? rascunho.attributes : [],
        });
        setSelectedMarketplaces(rascunhoMarketplaces || []);
      } else {
        setProductData(initialProductState);
        setSelectedMarketplaces([]);
      }
    }
  }, [isOpen, product, isCopy, rascunho]);

  // Carrega categorias do banco ao abrir o modal
  useEffect(() => {
    if (isOpen) {
      apiClient.get('/categories').then(res => {
        setCategories(res.data.categories || []);
      }).catch(() => {});
    }
  }, [isOpen]);

  // Sync do search com o valor do campo
  useEffect(() => {
    setCategorySearch(productData.category || '');
  }, [productData.category]);

  const filteredCategories = categorySearch
    ? categories.filter(c => c.toLowerCase().includes(categorySearch.toLowerCase()))
    : categories;

  const suggestCategoryWithAI = async () => {
    if (aiLocked) return;
    if (!productData.title && !productData.brand) {
      alert('Preencha pelo menos Título ou Marca para a IA sugerir uma categoria.');
      return;
    }
    if (categories.length === 0) {
      alert('Nenhuma categoria cadastrada ainda. Cadastre categorias na tela de Mapeamento primeiro.');
      return;
    }
    setAiLocked(true);
    setGeneratingCategory(true);
    try {
      const response = await apiClient.post('/ai/suggest-category', {
        title: productData.title,
        brand: productData.brand,
        description: productData.description,
        categories,
      }, AI_REQ);
      if (response.data?.category) {
        setProductData(prev => ({ ...prev, category: response.data.category }));
        setCategorySearch(response.data.category);
      }
    } catch (e: any) {
      console.error('Erro ao sugerir categoria:', e);
      const msg = e?.response?.data?.error || e?.message || 'Erro desconhecido';
      alert(`❌ Falha ao sugerir categoria com IA:\n${msg}`);
    } finally {
      setGeneratingCategory(false);
      setAiLocked(false);
    }
  };

  const getStrictestRules = () => {
    const activeRules = selectedMarketplaces.length > 0
        ? selectedMarketplaces.map(id => MARKETPLACE_IMAGE_RULES[id]).filter(Boolean)
        : [{ minWidth: 500, minHeight: 500, maxSizeMB: 5, name: 'Padrão' }];

    return {
      minWidth:  Math.max(...activeRules.map(r => r.minWidth)),
      minHeight: Math.max(...activeRules.map(r => r.minHeight)),
      maxSizeMB: Math.min(...activeRules.map(r => r.maxSizeMB)),
      activeNames: activeRules.map(r => r.name),
    };
  };

  const validateImage = (file: File): Promise<boolean> => {
    return new Promise((resolve) => {
      const rules = getStrictestRules();
      const activeRules = selectedMarketplaces.length > 0
        ? selectedMarketplaces.map(id => MARKETPLACE_IMAGE_RULES[id]).filter(Boolean)
        : [];

      // 1. Tamanho do arquivo
      const fileSizeMB = file.size / (1024 * 1024);
      if (fileSizeMB > rules.maxSizeMB) {
        alert(`❌ Arquivo muito grande (${fileSizeMB.toFixed(2)}MB).\n\nTamanho máximo permitido: ${rules.maxSizeMB}MB.`);
        resolve(false);
        return;
      }

      if (!file.type.startsWith('image/')) {
        resolve(true);
        return;
      }

      const img = new Image();
      img.onload = () => {
        const w = img.width;
        const h = img.height;
        const warnings: string[] = [];

        // 2. Resolução mínima (bloqueia)
        if (w < rules.minWidth || h < rules.minHeight) {
          alert(`❌ Resolução muito baixa (${w}×${h}px).\n\nMínimo exigido pelos canais selecionados: ${rules.minWidth}×${rules.minHeight}px.\n\nUse uma imagem de maior qualidade.`);
          resolve(false);
          return;
        }

        // 3. Resolução recomendada (avisa mas não bloqueia)
        const recW = Math.max(...activeRules.map(r => r.recommendedWidth ?? 0));
        const recH = Math.max(...activeRules.map(r => r.recommendedHeight ?? 0));
        if (recW > 0 && (w < recW || h < recH)) {
          warnings.push(`⚠️ Resolução abaixo do recomendado (${w}×${h}px). Recomendado: ${recW}×${recH}px.\nImagens com baixa resolução podem ser reprovadas na análise do marketplace.`);
        }

        // 4. Proporção quadrada (avisa para ML, Shopee, Amazon, Magalu)
        const requiresSquare = activeRules.some(r => r.requireSquare);
        if (requiresSquare) {
          const ratio = w / h;
          if (ratio < 0.85 || ratio > 1.15) {
            warnings.push(`⚠️ Proporção da imagem não é quadrada (${w}×${h}px).\nMercado Livre, Shopee e Amazon exigem imagens próximas de 1:1 (quadradas).\nImagens fora do padrão podem ser reprovadas.`);
          }
        }

        // 5. Aviso fundo branco
        const requiresWhite = activeRules.some(r => r.requireWhiteBackground);
        if (requiresWhite) {
          warnings.push(`ℹ️ Mercado Livre e Amazon exigem fundo branco ou claro, sem texto, marca d'água ou bordas sobrepostas.\nVerifique se a imagem atende a esse requisito.`);
        }

        if (warnings.length > 0) {
          const proceed = window.confirm(`${warnings.join('\n\n')}\n\nDeseja continuar mesmo assim?`);
          resolve(proceed);
        } else {
          resolve(true);
        }
      };
      img.onerror = () => {
        alert('❌ Erro ao ler a imagem. Arquivo corrompido ou inválido.');
        resolve(false);
      };
      img.src = URL.createObjectURL(file);
    });
  };

  const handleFileUpload = async (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e || !e.target || !e.target.files || e.target.files.length === 0) return;

    const file = e.target.files[0];
    const isValid = await validateImage(file);
    if (!isValid) {
      e.target.value = '';
      return;
    }

    setUploadingMap(prev => ({ ...prev, [index]: true }));
    updateImage(index, 'A fazer upload...');

    const userCode = localStorage.getItem('userCode') || '';
    const formData = new FormData();
    formData.append('file', file);
    formData.append('userCode', userCode);

    try {
      const result = await apiClient.post('/products/upload-media', formData);
      const uploadedUrl = result.data.url;
      updateImage(index, uploadedUrl);

      // ── Diagnóstico ML: só se ML estiver nos allowedMarketplaces desta imagem ──
      const imageMarketplaces = productData.images[index]?.allowedMarketplaces || [];
      const mlSelected = imageMarketplaces.some(m => m.toLowerCase().includes('mercado') || m.toLowerCase() === 'ml');
      const mlCategoryId = productData.ml_category_id;

      if (mlSelected && mlCategoryId) {
        try {
          const diagResp = await apiClient.post('/ml/images/diagnostic', {
            picture_url:  resolveMediaUrl(uploadedUrl),
            category_id:  mlCategoryId,
            title:        productData.title || '',
            picture_type: index === 0 ? 'thumbnail' : 'other',
          });
          const diag = diagResp.data;
          setMlDiagnosticMap(prev => ({ ...prev, [index]: diag }));
        } catch (diagErr) {
          console.warn('Diagnóstico ML falhou (não bloqueia upload):', diagErr);
        }
      } else {
        // Limpa diagnóstico anterior se ML não está mais selecionado
        setMlDiagnosticMap(prev => { const n = { ...prev }; delete n[index]; return n; });
      }
    } catch (error: any) {
      console.error('Erro no upload:', error);
      updateImage(index, '');
      const msg = error?.response?.data?.error || 'Verifique as credenciais.';
      alert(`Erro ao subir ficheiro: ${msg}`);
    } finally {
      setUploadingMap(prev => ({ ...prev, [index]: false }));
      e.target.value = '';
    }
  };

  const generateTitleWithAI = async () => {
    if (aiLocked) return;
    if (!productData.brand && !productData.category && !productData.sku && !productData.description?.trim()) {
      alert('Preencha pelo menos Marca, Categoria, SKU ou Descrição para gerar um título com IA');
      return;
    }
    setAiLocked(true);
    setGeneratingTitle(true);
    try {
      const response = await apiClient.post('/ai/generate-title', {
        brand: productData.brand, category: productData.category, sku: productData.sku, attributes: productData.attributes, currentTitle: productData.title,
        // A descrição do lojista pode ter informação importante (volume, material, tamanho...)
        currentDescription: productData.description,
      }, AI_REQ);
      if (response.data && response.data.title) {
        setProductData(prev => ({ ...prev, title: response.data.title }));
      } else if (response.data?.error) {
        alert(`❌ Falha ao gerar título com IA:\n${response.data.error}`);
      }
    } catch (error: any) {
      console.error('Erro ao gerar título:', error);
      const msg = error?.response?.data?.error || error?.message || 'Erro desconhecido';
      alert(`❌ Falha ao gerar título com IA:\n${msg}`);
    } finally {
      setGeneratingTitle(false);
      setAiLocked(false);
    }
  };

  const generateDescriptionWithAI = async () => {
    if (aiLocked) return;
    if (!productData.title && !productData.brand && !productData.category && !productData.description?.trim()) {
      alert('Preencha pelo menos Título, Marca, Categoria ou Descrição para gerar uma descrição com IA');
      return;
    }
    setAiLocked(true);
    setGeneratingDescription(true);
    try {
      const response = await apiClient.post('/ai/generate-description', {
        title: productData.title, brand: productData.brand, category: productData.category, sku: productData.sku, attributes: productData.attributes, price: productData.basePrice, currentDescription: productData.description,
      }, AI_REQ);
      if (response.data && response.data.description) {
        setProductData(prev => ({ ...prev, description: response.data.description }));
      } else if (response.data?.error) {
        alert(`❌ Falha ao gerar descrição com IA:\n${response.data.error}`);
      }
    } catch (error: any) {
      console.error('Erro ao gerar descrição:', error);
      const msg = error?.response?.data?.error || error?.message || 'Erro desconhecido';
      alert(`❌ Falha ao gerar descrição com IA:\n${msg}`);
    } finally {
      setGeneratingDescription(false);
      setAiLocked(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const validImages = productData.images.filter(img => img.url.trim() !== '' && img.url !== 'A fazer upload...');
      const primaryImageUrl = validImages.length > 0 ? validImages[0].url : '';

      const formattedData: any = {
        ...productData,
        basePrice: parseFloat(productData.basePrice) || 0,
        stock: parseInt(productData.stock) || 0,
        weight: parseFloat(productData.weight || '0'),
        height: parseFloat(productData.height || '0'),
        width: parseFloat(productData.width || '0'),
        length: parseFloat(productData.length || '0'),
        marketplaces: selectedMarketplaces,
        image: primaryImageUrl,
        images: validImages.map(img => img.url),
        imagesConfig: validImages,
      };
      // Preço por marketplace: número, ou null quando vazio (usa o preço base)
      Object.keys(formattedData).forEach(k => {
        if (k.endsWith('_price') && k !== 'basePrice') {
          const n = parseFloat(String(formattedData[k] ?? '').replace(',', '.'));
          formattedData[k] = n > 0 ? n : null;
        }
      });

      if (editando && product && (product.id || product._id)) {
        const productId = (product.id || product._id) as string;
        await ProductService.updateProduct(productId, formattedData);
      } else {
        await ProductService.createProduct(formattedData);
      }

      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Erro ao salvar produto:', error);
      alert(error.message || 'Erro ao salvar produto.');
    } finally {
      setLoading(false);
    }
  };

  const addImage = () => setProductData(prev => ({
    ...prev,
    images: [...prev.images, { url: '', allowedMarketplaces: [...selectedMarketplaces] }]
  }));

  const removeImage = (index: number) => {
    setProductData(prev => {
      const newImages = prev.images.filter((_, i) => i !== index);
      return { ...prev, images: newImages.length > 0 ? newImages : [{ url: '', allowedMarketplaces: [...selectedMarketplaces] }] };
    });
  };

  const updateImage = (index: number, url: string) => setProductData(prev => ({
    ...prev,
    images: prev.images.map((img, i) => i === index ? { ...img, url } : img)
  }));

  const toggleImageMarketplace = (imgIndex: number, mpId: string) => {
    setProductData(prev => ({
      ...prev,
      images: prev.images.map((img, i) => {
        if (i !== imgIndex) return img;
        const isAllowed = img.allowedMarketplaces.includes(mpId);
        return {
          ...img,
          allowedMarketplaces: isAllowed
              ? img.allowedMarketplaces.filter(id => id !== mpId)
              : [...img.allowedMarketplaces, mpId]
        };
      })
    }));
  };

  const setAsPrimaryImage = (index: number) => {
    if (index === 0) return; // Já é a principal

    setProductData(prev => {
      const newImages = [...prev.images];
      const [selectedImage] = newImages.splice(index, 1); // Remove da posição atual
      newImages.unshift(selectedImage); // Coloca no topo (index 0)
      return { ...prev, images: newImages };
    });

    // Reajusta o estado de loading para acompanhar a imagem que mudou de lugar
    setUploadingMap(prev => {
      const newMap = { ...prev };
      const movingState = newMap[index];
      for (let i = index; i > 0; i--) {
        newMap[i] = newMap[i - 1];
      }
      newMap[0] = movingState;
      return newMap;
    });
  };

  const addAttribute = () => setProductData(prev => ({ ...prev, attributes: [...prev.attributes, { name: '', value: '', required: false }] }));
  const removeAttribute = (index: number) => setProductData(prev => ({ ...prev, attributes: prev.attributes.filter((_, i) => i !== index) }));
  const updateAttribute = (index: number, field: string, value: any) => setProductData(prev => ({ ...prev, attributes: prev.attributes.map((attr, i) => i === index ? { ...attr, [field]: value } : attr) }));

  if (!isOpen) return null;

  return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto">
        <div className="bg-dark-900 rounded-xl border border-dark-700 max-w-6xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">

          {/* Header */}
          <div className="sticky top-0 bg-dark-900 border-b border-dark-700 p-6 flex items-center justify-between z-10">
            <div>
              <h2 className="text-2xl font-bold text-dark-100">
                {isCopy ? 'Duplicar Produto' : product ? 'Editar Produto' : 'Adicionar Produto'}
              </h2>
              <p className="text-dark-400 text-sm mt-1">
                Passo {currentStep} de 4 - {
                currentStep === 1 ? 'Informações Básicas' :
                    currentStep === 2 ? 'Marketplaces e Campos Específicos' :
                        currentStep === 3 ? 'Dimensões e Imagens' :
                            'Atributos e Revisão'
              }
              </p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-dark-800 rounded-lg transition-colors text-dark-400 hover:text-dark-100">
              <X className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="p-6">

              {/* STEP 1: INFORMAÇÕES BÁSICAS */}
              {currentStep === 1 && (
                  <div className="space-y-6 animate-fade-in">
                    {isCopy && (
                      <div className="p-3 rounded-lg border border-amber-300 bg-amber-50 text-sm text-amber-900">
                        <strong>Cópia de {product?.sku}.</strong> Os dados, fotos e marketplaces foram copiados.
                        O <strong>SKU</strong> já vem com o próximo número livre (depois de salvo ele não muda).
                        Troque o <strong>tamanho/cor</strong> (atributos e ficha técnica), o <strong>EAN</strong> e o
                        <strong> estoque</strong>. Ao salvar, é criado um produto e anúncios novos.
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-dark-300 mb-2">SKU / Código *</label>
                        <input
                            type="text" required disabled={editando} value={productData.sku}
                            onChange={(e) => setProductData(prev => ({ ...prev, sku: e.target.value.replace(/\s/g, '') }))}
                            className={`w-full px-4 py-3 bg-dark-800 border border-dark-700 rounded-lg text-dark-100 focus:ring-2 focus:ring-primary-500 outline-none ${editando ? 'opacity-50 cursor-not-allowed' : ''} ${isCopy ? 'ring-2 ring-amber-400' : ''}`}
                            placeholder="ex: PROD-001"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-dark-300 mb-2">
                          EAN / Código de barras
                          {productData.ean && (eanValido(productData.ean)
                            ? <span className="ml-2 text-xs text-emerald-400">✓ válido</span>
                            : <span className="ml-2 text-xs text-red-400">dígito verificador não confere</span>)}
                        </label>
                        <input
                            type="text" inputMode="numeric" maxLength={14} value={productData.ean}
                            onChange={(e) => {
                              const v = e.target.value.replace(/\D/g, '');
                              setProductData(prev => ({ ...prev, ean: v, magalu_ean: v, casas_ean: v, carrefour_ean: v, leroy_ean: v }));
                            }}
                            className={`w-full px-4 py-3 bg-dark-800 border rounded-lg text-dark-100 focus:ring-2 focus:ring-primary-500 outline-none ${
                              productData.ean && !eanValido(productData.ean) ? 'border-red-500/60' : 'border-dark-700'}`}
                            placeholder="ex: 7891234567895 (13 dígitos da embalagem)"
                        />
                        <p className="text-xs text-dark-500 mt-1">Vale para todos os marketplaces. Sem código? Deixe em branco — nunca invente.</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-dark-300 mb-2">Marca *</label>
                        <input
                            type="text" required value={productData.brand}
                            onChange={(e) => setProductData(prev => ({ ...prev, brand: e.target.value }))}
                            className="w-full px-4 py-3 bg-dark-800 border border-dark-700 rounded-lg text-dark-100 focus:ring-2 focus:ring-primary-500 outline-none"
                        />
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-dark-300">Título do Produto *</label>
                        <button type="button" onClick={generateTitleWithAI} disabled={aiLocked} className={clsx("flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all", aiLocked ? "bg-purple-500/20 text-purple-400 cursor-wait" : "bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-dark-100 shadow-lg shadow-purple-500/20")}>
                          {generatingTitle ? <><Loader className="w-3.5 h-3.5 animate-spin" /> A gerar...</> : <><Sparkles className="w-3.5 h-3.5" /> Gerar com IA</>}
                        </button>
                      </div>
                      <input type="text" required value={productData.title} onChange={(e) => setProductData(prev => ({ ...prev, title: e.target.value }))} className="w-full px-4 py-3 bg-dark-800 border border-dark-700 rounded-lg text-dark-100 focus:ring-2 focus:ring-primary-500 outline-none" maxLength={60}/>
                      <div className="flex items-center justify-between mt-1">
                        <span className={clsx("text-xs", productData.title.length > 55 ? "text-yellow-400" : "text-dark-500")}>
                          {productData.title.length}/60
                        </span>
                        {productData.brand && productData.title.toLowerCase().startsWith(productData.brand.toLowerCase()) && (
                          <span className="text-xs text-yellow-400">
                            ⚠️ Marca no início do título ocupa espaço — remova-a (já vai no campo Marca)
                          </span>
                        )}
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-dark-300">Descrição *</label>
                        <button type="button" onClick={generateDescriptionWithAI} disabled={aiLocked} className={clsx("flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all", aiLocked ? "bg-purple-500/20 text-purple-400 cursor-wait" : "bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-dark-100 shadow-lg shadow-purple-500/20")}>
                          {generatingDescription ? <><Loader className="w-3.5 h-3.5 animate-spin" /> A gerar...</> : <><Wand2 className="w-3.5 h-3.5" /> Gerar com IA</>}
                        </button>
                      </div>
                      <textarea required value={productData.description} onChange={(e) => setProductData(prev => ({ ...prev, description: e.target.value }))} rows={6} className="w-full px-4 py-3 bg-dark-800 border border-dark-700 rounded-lg text-dark-100 focus:ring-2 focus:ring-primary-500 outline-none" maxLength={5000}/>
                      <p className="text-xs text-dark-500 mt-1">{productData.description.length}/5000</p>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div className="relative">
                        <div className="flex items-center justify-between mb-2">
                          <label className="block text-sm font-medium text-dark-300">Categoria *</label>
                          <button
                            type="button"
                            onClick={suggestCategoryWithAI}
                            disabled={aiLocked}
                            className={clsx(
                              "flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium transition-all",
                              aiLocked
                                ? "bg-purple-500/20 text-purple-400 cursor-wait"
                                : "bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-dark-100 shadow-lg shadow-purple-500/20"
                            )}
                          >
                            {generatingCategory
                              ? <><Loader className="w-3 h-3 animate-spin" /> Sugerindo...</>
                              : <><Sparkles className="w-3 h-3" /> Sugerir IA</>
                            }
                          </button>
                        </div>
                        <input
                          type="text"
                          required
                          value={categorySearch}
                          onChange={(e) => {
                            setCategorySearch(e.target.value);
                            setProductData(prev => ({ ...prev, category: e.target.value }));
                            setShowCategoryDropdown(true);
                          }}
                          onFocus={() => setShowCategoryDropdown(true)}
                          onBlur={() => setTimeout(() => setShowCategoryDropdown(false), 150)}
                          placeholder="Digite ou selecione..."
                          className="w-full px-4 py-3 bg-dark-800 border border-dark-700 rounded-lg text-dark-100 focus:ring-2 focus:ring-primary-500 outline-none"
                        />
                        {showCategoryDropdown && filteredCategories.length > 0 && (
                          <ul className="absolute z-50 mt-1 w-full bg-dark-800 border border-dark-600 rounded-lg shadow-xl max-h-48 overflow-y-auto">
                            {filteredCategories.map((cat) => (
                              <li
                                key={cat}
                                onMouseDown={() => {
                                  setProductData(prev => ({ ...prev, category: cat }));
                                  setCategorySearch(cat);
                                  setShowCategoryDropdown(false);
                                }}
                                className={clsx(
                                  "px-4 py-2.5 text-sm cursor-pointer transition-colors",
                                  productData.category === cat
                                    ? "bg-primary-500/20 text-primary-300"
                                    : "text-dark-200 hover:bg-dark-700"
                                )}
                              >
                                {cat}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                      <div>
                        <label className="flex items-center gap-1.5 text-sm font-medium text-dark-300 mb-2">
                          Preço (R$) *
                          {temMercadoLivre(selectedMarketplaces) && <BlackFridayPriceHint precoSubiu={mlPrecoSubiu} />}
                        </label>
                        <input type="number" required step="0.01" min="0" value={productData.basePrice} onChange={(e) => setProductData(prev => ({ ...prev, basePrice: e.target.value }))} className="w-full px-4 py-3 bg-dark-800 border border-dark-700 rounded-lg text-dark-100 focus:ring-2 focus:ring-primary-500 outline-none"/>
                        {mlPrecoSubiu && (
                          <p className="mt-1 text-xs text-red-700">
                            Preço acima do atual ({precoOriginal?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}) — no Mercado Livre isso tira o produto da Black Friday.
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-dark-300 mb-2">Estoque *</label>
                        <input type="number" required min="0" value={productData.stock} onChange={(e) => setProductData(prev => ({ ...prev, stock: e.target.value }))} className="w-full px-4 py-3 bg-dark-800 border border-dark-700 rounded-lg text-dark-100 focus:ring-2 focus:ring-primary-500 outline-none"/>
                      </div>
                    </div>
                  </div>
              )}

              {/* STEP 2: MARKETPLACES E CAMPOS ESPECÍFICOS */}
              {currentStep === 2 && (
                  <div className="space-y-6 animate-fade-in">
                    <div>
                      <label className="block text-lg font-semibold text-dark-100 mb-4">Selecione os Marketplaces *</label>

                      {['Gigantes', 'Grupo Casas Bahia', 'Casa e Construção', 'Moda e Esporte', 'Supermercados'].map(category => (
                          <div key={category} className="mb-6">
                            <h4 className="text-sm font-medium text-dark-400 mb-3 uppercase tracking-wider">{category}</h4>
                            <div className="grid grid-cols-3 gap-3">
                              {MARKETPLACES.filter(m => m.category === category).map((marketplace) => (
                                  <button
                                      key={marketplace.id} type="button"
                                      onClick={() => {
                                        setSelectedMarketplaces(prev => {
                                          const isSelected = prev.includes(marketplace.id);
                                          const newSelected = isSelected ? prev.filter(id => id !== marketplace.id) : [...prev, marketplace.id];

                                          // Propaga automaticamente para as imagens
                                          setProductData(pd => ({
                                            ...pd,
                                            images: pd.images.map(img => ({
                                              ...img,
                                              allowedMarketplaces: isSelected
                                                  ? img.allowedMarketplaces.filter(id => id !== marketplace.id)
                                                  : [...img.allowedMarketplaces, marketplace.id]
                                            }))
                                          }));
                                          return newSelected;
                                        });
                                      }}
                                      className={clsx('p-4 rounded-lg border-2 transition-all text-left', selectedMarketplaces.includes(marketplace.id) ? 'border-primary-500 bg-primary-500/10' : 'border-dark-700 bg-dark-800 hover:border-dark-600')}
                                  >
                                    <div className="flex items-center justify-between mb-2">
                                      <span className="font-semibold text-dark-100 text-sm">{marketplace.name}</span>
                                      {selectedMarketplaces.includes(marketplace.id) && (
                                          <div className="w-5 h-5 rounded-full bg-primary-500 flex items-center justify-center flex-shrink-0">
                                            <Save className="w-3 h-3 text-dark-100" />
                                          </div>
                                      )}
                                    </div>
                                    <div className="w-full h-1 rounded-full" style={{ backgroundColor: marketplace.color }} />
                                  </button>
                              ))}
                            </div>
                          </div>
                      ))}
                    </div>

                    {/* Preço por marketplace — vazio usa o preço base */}
                    {selectedMarketplaces.length > 0 && (
                        <div className="p-4 bg-dark-800/50 rounded-lg border border-dark-700 mt-6">
                          <h3 className="text-lg font-semibold text-dark-100 mb-1">💲 Preço por marketplace</h3>
                          <p className="text-xs text-dark-400 mb-4">
                            Deixe em branco para usar o preço base (R$ {productData.basePrice || '0,00'}).
                            Útil para compensar comissões e fretes diferentes em cada canal.
                          </p>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            {selectedMarketplaces.map(mpId => {
                              const campo = `${mpId}_price`;
                              const valor = String((productData as any)[campo] ?? '');
                              const base = parseFloat(String(productData.basePrice).replace(',', '.')) || 0;
                              const v = parseFloat(valor.replace(',', '.')) || 0;
                              const dif = base && v ? Math.round(((v - base) / base) * 1000) / 10 : null;
                              return (
                                <div key={campo}>
                                  <label className="flex items-center gap-1.5 text-xs font-medium text-dark-300 mb-1">
                                    {MARKETPLACES.find(m => m.id === mpId)?.name || mpId}
                                    {mpId === 'ml' && <BlackFridayPriceHint />}
                                  </label>
                                  <input type="number" step="0.01" min="0" value={valor}
                                         placeholder={productData.basePrice ? `${productData.basePrice} (base)` : 'preço base'}
                                         onChange={e => setProductData(prev => ({ ...prev, [campo]: e.target.value }))}
                                         className="w-full px-3 py-2 bg-dark-900 border border-dark-700 rounded-lg text-dark-100 text-sm focus:ring-2 focus:ring-primary-500 outline-none" />
                                  {dif !== null && dif !== 0 && (
                                    <p className={`text-[11px] mt-0.5 ${dif > 0 ? 'text-emerald-700' : 'text-amber-800'}`}>
                                      {dif > 0 ? '+' : ''}{dif}% em relação ao base
                                    </p>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                    )}

                    {/* ======================================================= */}
                    {/* BLOCOS DINÂMICOS DE CAMPOS ESPECÍFICOS (NOVOS INCLUÍDOS) */}
                    {/* ======================================================= */}

                    {selectedMarketplaces.includes('ml') && (
                        <div className="p-4 bg-dark-800/50 rounded-lg border border-dark-700 mt-6">
                          <h3 className="text-lg font-semibold text-dark-100 mb-4 flex items-center gap-2">🛒 Mercado Livre</h3>
                          <BlackFridayPriceHint variant="banner" precoSubiu={mlPrecoSubiu} className="mb-4" />
                          <div className="grid grid-cols-2 gap-4">
                            <div className="col-span-2 relative">
                              <label className="block text-sm font-medium text-dark-300 mb-2">
                                ID da Categoria ML *
                                {mlSearching && <span className="ml-2 text-xs text-dark-400">buscando…</span>}
                              </label>
                              <input
                                type="text"
                                value={productData.ml_category_id}
                                onChange={e => searchMlCategories(e.target.value)}
                                onBlur={() => setTimeout(() => setMlOpen(false), 200)}
                                onFocus={() => mlSuggestions.length > 0 && setMlOpen(true)}
                                className="w-full px-4 py-3 bg-dark-900 border border-dark-700 rounded-lg text-dark-100"
                                placeholder="Digite para buscar categoria (ex: celular, notebook…)"
                              />
                              {mlOpen && mlSuggestions.length > 0 && (
                                <ul className="absolute z-50 w-full mt-1 bg-dark-800 border border-dark-600 rounded-lg shadow-xl max-h-60 overflow-auto">
                                  {mlSuggestions.map(s => (
                                    <li key={s.category_id}
                                      onMouseDown={() => pickMlSuggestion(s)}
                                      className="px-4 py-3 cursor-pointer hover:bg-dark-700 border-b border-dark-700 last:border-0"
                                    >
                                      <p className="text-dark-100 text-sm font-medium">
                                        {s.caminho || s.category_name || s.category_title || s.domain_name}
                                      </p>
                                      <p className="text-dark-400 text-xs">{s.category_id}</p>
                                    </li>
                                  ))}
                                </ul>
                              )}
                              {mlCaminho && (
                                <p className="text-xs text-emerald-400 mt-1">Categoria: {mlCaminho}</p>
                              )}
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-dark-300 mb-2">Domain ID (Catálogo ML)</label>
                              <input type="text" value={productData.ml_domain_id} onChange={e => setProductData(p => ({...p, ml_domain_id: e.target.value}))} className="w-full px-4 py-3 bg-dark-900 border border-dark-700 rounded-lg text-dark-100" placeholder="Preenchido automaticamente ao selecionar categoria" />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-dark-300 mb-2 flex justify-between">
                                <span>Family Name (variantes)</span>
                                <span className={`text-xs ${productData.ml_family_name.length > 230 ? 'text-yellow-400' : 'text-dark-500'}`}>{productData.ml_family_name.length}/255</span>
                              </label>
                              <div className="flex gap-2">
                                <input type="text" maxLength={255} value={productData.ml_family_name} onChange={e => setProductData(p => ({...p, ml_family_name: e.target.value}))} className="flex-1 px-4 py-3 bg-dark-900 border border-dark-700 rounded-lg text-dark-100" placeholder="ex: Camiseta Básica Logo QFlow (igual para P/M/G)" />
                                <button
                                  type="button"
                                  title="Sugerir com IA"
                                  onClick={async () => {
                                    try {
                                      const res = await apiClient.post('/ai/suggest-family-name', {
                                        title: productData.title,
                                        brand: productData.brand,
                                        category: productData.category,
                                        description: productData.description,
                                        attributes: productData.attributes,
                                      }, AI_REQ);
                                      if (res.data?.familyName) setProductData(p => ({...p, ml_family_name: res.data.familyName}));
                                    } catch { /* silencia erros de rede */ }
                                  }}
                                  className="px-3 py-2 bg-primary-500/10 hover:bg-primary-500/20 border border-primary-500/30 rounded-lg text-primary-400 text-sm transition-colors whitespace-nowrap"
                                >✨ IA</button>
                              </div>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-dark-300 mb-2">Condição do Produto</label>
                              <select value={productData.ml_condition} onChange={(e) => setProductData(prev => ({ ...prev, ml_condition: e.target.value }))} className="w-full px-4 py-3 bg-dark-900 border border-dark-700 rounded-lg text-dark-100 focus:ring-primary-500">
                                <option value="new">Novo</option>
                                <option value="used">Usado</option>
                              </select>
                            </div>
                            <div className="col-span-2">
                              <label className="block text-sm font-medium text-dark-300 mb-3">Tipo de Anúncio</label>
                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                {[
                                  { value: 'free',         label: 'Grátis',         commission: '0%',  rep: 'Nenhuma',   color: 'text-dark-400',   border: 'border-dark-600' },
                                  { value: 'bronze',       label: 'Bronze',         commission: '~10%', rep: 'Baixa',     color: 'text-orange-400',  border: 'border-orange-500/30' },
                                  { value: 'gold_special', label: 'Ouro Especial',  commission: '~14%', rep: 'Boa',       color: 'text-yellow-400',  border: 'border-yellow-500/30' },
                                  { value: 'gold_pro',     label: 'Ouro Pro',       commission: '~16%', rep: 'Excelente', color: 'text-amber-300',   border: 'border-amber-400/40' },
                                ].map(tier => (
                                  <button
                                    key={tier.value}
                                    type="button"
                                    onClick={() => setProductData(prev => ({ ...prev, ml_listing_type: tier.value }))}
                                    className={`flex flex-col items-start p-3 rounded-lg border transition-all text-left ${
                                      productData.ml_listing_type === tier.value
                                        ? `${tier.border} bg-dark-800 ring-1 ring-inset ${tier.border}`
                                        : 'border-dark-700 bg-dark-900 hover:bg-dark-800'
                                    }`}
                                  >
                                    <span className={`text-xs font-semibold ${tier.color}`}>{tier.label}</span>
                                    <span className="text-dark-100 font-bold text-sm mt-1">{tier.commission}</span>
                                    <span className="text-dark-500 text-xs mt-0.5">Rep: {tier.rep}</span>
                                  </button>
                                ))}
                              </div>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-dark-300 mb-2">Garantia</label>
                              <input type="text" value={productData.ml_warranty} onChange={(e) => setProductData(prev => ({ ...prev, ml_warranty: e.target.value }))} className="w-full px-4 py-3 bg-dark-900 border border-dark-700 rounded-lg text-dark-100 placeholder-dark-500 focus:ring-primary-500" placeholder="ex: 12 meses" />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-dark-300 mb-2">Tabela de medidas ML</label>
                              <input type="text" value={productData.ml_size_grid_id}
                                     onChange={(e) => setProductData(prev => ({ ...prev, ml_size_grid_id: e.target.value.replace(/\D/g, '') }))}
                                     className="w-full px-4 py-3 bg-dark-900 border border-dark-700 rounded-lg text-dark-100 placeholder-dark-500 focus:ring-primary-500"
                                     placeholder="ex: 4339173 (roupas e calçados)" />
                              <p className="text-xs text-dark-500 mt-1">Obrigatória em roupas/calçados. Crie no ML em Vendas → Tabelas de medidas e cole o ID aqui.</p>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-dark-300 mb-2">ID do Vídeo (YouTube)</label>
                              <input type="text" value={productData.ml_video_id} onChange={(e) => setProductData(prev => ({ ...prev, ml_video_id: e.target.value }))} className="w-full px-4 py-3 bg-dark-900 border border-dark-700 rounded-lg text-dark-100 placeholder-dark-500 focus:ring-primary-500" placeholder="ex: dQw4w9WgXcQ" />
                            </div>
                            <MlFichaTecnica
                              categoryId={productData.ml_category_id}
                              produto={{ title: productData.title, description: productData.description, brand: productData.brand, size: (productData as any).size, category: productData.category }}
                              attributes={productData.attributes}
                              onChange={attrs => setProductData(prev => ({ ...prev, attributes: attrs }))}
                              onFaltam={setMlFaltam}
                              onUsarCategoria={id => { setProductData(prev => ({ ...prev, ml_category_id: id })); mlCaminhoCategoria(id).then(setMlCaminho); }}
                            />
                          </div>
                        </div>
                    )}

                    {selectedMarketplaces.includes('amazon') && (
                        <div className="p-4 bg-dark-800/50 rounded-lg border border-dark-700 mt-4">
                          <h3 className="text-lg font-semibold text-dark-100 mb-4 flex items-center gap-2" style={{color: '#FF9900'}}>📦 Amazon</h3>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="col-span-2">
                              <label className="text-sm font-medium text-dark-300 mb-2 block">
                                Node ID da Categoria (Browse Node)
                                <a href="https://sellercentral.amazon.com.br/hz/approveProductClassificationTool/ref=xx_approve_dnav_xx" target="_blank" rel="noopener noreferrer" className="ml-2 text-xs text-primary-400 hover:underline">ver categorias →</a>
                              </label>
                              <input type="text" value={productData.amazon_category_id} onChange={e => setProductData(p => ({...p, amazon_category_id: e.target.value}))} className="w-full px-4 py-3 bg-dark-900 border border-dark-700 rounded-lg text-dark-100" placeholder="ex: 16028311011 (Eletrônicos)" />
                            </div>
                            <div>
                              <label className="text-sm font-medium text-dark-300 mb-2 block">ASIN (Se já existir na Amazon)</label>
                              <input type="text" value={productData.amazon_asin} onChange={e => setProductData(p => ({...p, amazon_asin: e.target.value}))} className="w-full px-4 py-3 bg-dark-900 border border-dark-700 rounded-lg text-dark-100" placeholder="B08N5M7S6K" />
                            </div>
                            <div>
                              <label className="text-sm font-medium text-dark-300 mb-2 block">Condição</label>
                              <select value={productData.amazon_condition} onChange={e => setProductData(p => ({...p, amazon_condition: e.target.value}))} className="w-full px-4 py-3 bg-dark-900 border border-dark-700 rounded-lg text-dark-100">
                                <option value="New">Novo</option>
                                <option value="UsedLikeNew">Usado (Como Novo)</option>
                              </select>
                            </div>
                          </div>
                        </div>
                    )}

                    {selectedMarketplaces.includes('shopee') && (
                        <div className="p-4 bg-dark-800/50 rounded-lg border border-dark-700 mt-4">
                          <h3 className="text-lg font-semibold text-dark-100 mb-4 flex items-center gap-2" style={{color: '#EE4D2D'}}>🛍️ Shopee</h3>
                          <div className="grid grid-cols-3 gap-4">
                            {/* Categoria */}
                            <div className="col-span-3">
                              <label className="text-sm font-medium text-dark-300 mb-2 block">Categoria Shopee *</label>
                              <CategoryAutocomplete
                                marketplace="shopee"
                                value={productData.shopee_category_id}
                                onChange={v => {
                                  setProductData(p => ({...p, shopee_category_id: v}));
                                  setShopeeBrands([]);
                                  setBrandRegisterMsg('');
                                }}
                                onPick={id => fetchShopeeBrands(id)}
                                placeholder="Digite para buscar (ex: vestido bebê, limpador, fone)"
                              />
                            </div>

                            {/* Marca Shopee */}
                            <div className="col-span-3">
                              <label className="text-sm font-medium text-dark-300 mb-2 block">
                                Marca (Shopee)
                                {shopeeBrandsLoading && <span className="ml-2 text-xs text-dark-400">buscando marcas...</span>}
                              </label>
                              <div className="relative">
                                <input
                                  type="text"
                                  value={shopeeBrandSearch || (productData as any).shopee_brand_name || productData.brand}
                                  onChange={e => { setShopeeBrandSearch(e.target.value); setShopeeBrandOpen(true); }}
                                  onFocus={() => { if (!shopeeBrands.length && productData.shopee_category_id) fetchShopeeBrands(productData.shopee_category_id); setShopeeBrandOpen(true); }}
                                  onBlur={() => setTimeout(() => setShopeeBrandOpen(false), 200)}
                                  className="w-full px-4 py-3 bg-dark-900 border border-dark-700 rounded-lg text-dark-100"
                                  placeholder="Digite para buscar marca..."
                                />
                                {shopeeBrandOpen && shopeeBrands.length > 0 && (
                                  <div className="absolute z-50 w-full mt-1 bg-dark-800 border border-dark-600 rounded-lg shadow-xl max-h-48 overflow-y-auto">
                                    {shopeeBrands
                                      .filter(b => !shopeeBrandSearch || b.display_brand_name.toLowerCase().includes(shopeeBrandSearch.toLowerCase()))
                                      .map(b => (
                                        <button key={b.brand_id} type="button"
                                          onMouseDown={() => {
                                            // Marca só da Shopee — não altera a marca geral (usada no ML, Magalu etc.)
                                            setProductData(p => ({...p, shopee_brand_name: b.display_brand_name} as any));
                                            setShopeeBrandSearch('');
                                            setShopeeBrandOpen(false);
                                          }}
                                          className="w-full text-left px-4 py-2 hover:bg-dark-700 text-dark-100 text-sm">
                                          {b.display_brand_name}
                                          {b.brand_id === 0 && <span className="ml-2 text-dark-400 text-xs">(sem marca)</span>}
                                        </button>
                                      ))}
                                  </div>
                                )}
                              </div>
                              {/* Botão solicitar registro */}
                              {shopeeBrands.length > 0 && !(productData as any).shopee_brand_name && productData.brand && !shopeeBrands.some(b => b.display_brand_name.toLowerCase() === productData.brand.toLowerCase()) && (
                                <div className="mt-2 flex items-center gap-3">
                                  <span className="text-xs text-yellow-400">Marca "{productData.brand}" não encontrada na Shopee.</span>
                                  <button
                                    type="button"
                                    onClick={registerShopeeBrand}
                                    disabled={registeringBrand}
                                    className="px-3 py-1 text-xs bg-orange-600 hover:bg-orange-500 text-white rounded-lg disabled:opacity-50 flex items-center gap-1"
                                  >
                                    {registeringBrand ? <Loader size={12} className="animate-spin" /> : <Plus size={12} />}
                                    Solicitar registro na Shopee
                                  </button>
                                </div>
                              )}
                              {brandRegisterMsg && <p className="mt-2 text-xs">{brandRegisterMsg}</p>}
                            </div>

                            {/* Tabela de medidas Shopee (obrigatória em moda) */}
                            <ShopeeTabelaMedidas
                              categoryId={productData.shopee_category_id}
                              tabelaId={(productData as any).shopee_size_chart_id || ''}
                              imagem={(productData as any).shopee_size_chart_image || ''}
                              onChange={(campos) => setProductData(p => ({ ...p, ...campos } as any))}
                            />

                            {/* Condição / Pré-venda / Dias */}
                            <div>
                              <label className="text-sm font-medium text-dark-300 mb-2 block">Condição</label>
                              <select value={productData.shopee_condition} onChange={e => setProductData(p => ({...p, shopee_condition: e.target.value}))} className="w-full px-4 py-3 bg-dark-900 border border-dark-700 rounded-lg text-dark-100">
                                <option value="NEW">Novo</option>
                                <option value="USED">Usado</option>
                              </select>
                            </div>
                            <div>
                              <label className="text-sm font-medium text-dark-300 mb-2 block">Pré-venda?</label>
                              <select value={productData.shopee_pre_order} onChange={e => setProductData(p => ({...p, shopee_pre_order: e.target.value}))} className="w-full px-4 py-3 bg-dark-900 border border-dark-700 rounded-lg text-dark-100">
                                <option value="false">Não</option>
                                <option value="true">Sim</option>
                              </select>
                            </div>
                            <div>
                              <label className="text-sm font-medium text-dark-300 mb-2 block">Dias para Envio</label>
                              <input type="number" min="0" max="30" value={productData.shopee_days_to_ship} onChange={e => setProductData(p => ({...p, shopee_days_to_ship: e.target.value}))} className="w-full px-4 py-3 bg-dark-900 border border-dark-700 rounded-lg text-dark-100" placeholder="Ex: 3" />
                            </div>
                          </div>
                        </div>
                    )}

                    {selectedMarketplaces.includes('americanas') && (
                        <div className="p-4 bg-dark-800/50 rounded-lg border border-dark-700 mt-4">
                          <h3 className="text-lg font-semibold text-dark-100 mb-4 flex items-center gap-2" style={{color: '#E60014'}}>🔴 Americanas (B2W)</h3>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-dark-300 mb-2">Tempo de Crossdocking (Dias)</label>
                              <input type="number" min="0" value={productData.americanas_crossdocking} onChange={e => setProductData(p => ({...p, americanas_crossdocking: e.target.value}))} className="w-full px-4 py-3 bg-dark-900 border border-dark-700 rounded-lg text-dark-100" placeholder="0" />
                            </div>
                          </div>
                        </div>
                    )}

                    {selectedMarketplaces.includes('tiktok') && (
                        <div className="p-4 bg-dark-800/50 rounded-lg border border-dark-700 mt-4">
                          <h3 className="text-lg font-semibold text-dark-100 mb-4 flex items-center gap-2">🎵 TikTok Shop</h3>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="col-span-2">
                              <label className="block text-sm font-medium text-dark-300 mb-2">
                                Categoria TikTok Shop *
                              </label>
                              <TikTokCategoryAutocomplete
                                value={productData.tiktok_category_id}
                                onChange={val => setProductData(p => ({...p, tiktok_category_id: val}))}
                              />
                              <p className="text-xs text-dark-500 mt-1">Obrigatório para publicar no TikTok Shop. Digite para buscar pelo nome ou ID da categoria.</p>
                            </div>
                          </div>
                        </div>
                    )}

                    {selectedMarketplaces.includes('shein') && (
                        <div className="p-4 bg-dark-800/50 rounded-lg border border-dark-700 mt-4">
                          <h3 className="text-lg font-semibold text-dark-100 mb-4 flex items-center gap-2">✨ Shein</h3>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-dark-300 mb-2">Material</label>
                              <input type="text" value={productData.shein_material} onChange={e => setProductData(p => ({...p, shein_material: e.target.value}))} className="w-full px-4 py-3 bg-dark-900 border border-dark-700 rounded-lg text-dark-100" placeholder="ex: 100% Algodão" />
                            </div>
                          </div>
                        </div>
                    )}

                    {(selectedMarketplaces.includes('magalu') || selectedMarketplaces.includes('carrefour') || selectedMarketplaces.includes('leroy') || selectedMarketplaces.includes('pontofrio') || selectedMarketplaces.includes('extra') || selectedMarketplaces.includes('casasbahia')) && (
                        <div className="p-4 bg-dark-800/50 rounded-lg border border-dark-700 mt-4">
                          <h3 className="text-lg font-semibold text-dark-100 mb-4 flex items-center gap-2">🏪 Varejo (Magalu, Carrefour, Leroy, Casas Bahia, Ponto Frio, Extra)</h3>
                          <div className="grid grid-cols-3 gap-4">
                            {selectedMarketplaces.includes('magalu') && (
                              <div>
                                <label className="block text-sm font-medium text-dark-300 mb-2">Categoria Magalu</label>
                                <CategoryAutocomplete
                                  marketplace="magalu"
                                  value={productData.magalu_category_id}
                                  onChange={val => setProductData(p => ({ ...p, magalu_category_id: val }))}
                                  placeholder="Digite para buscar categoria…"
                                />
                              </div>
                            )}
                            {selectedMarketplaces.includes('carrefour') && (
                              <div>
                                <label className="block text-sm font-medium text-dark-300 mb-2">Categoria Carrefour</label>
                                <CategoryAutocomplete
                                  marketplace="carrefour"
                                  value={productData.carrefour_category_id}
                                  onChange={val => setProductData(p => ({ ...p, carrefour_category_id: val }))}
                                  placeholder="Digite para buscar categoria…"
                                />
                              </div>
                            )}
                            {selectedMarketplaces.includes('leroy') && (
                              <div>
                                <label className="block text-sm font-medium text-dark-300 mb-2">Categoria Leroy Merlin</label>
                                <CategoryAutocomplete
                                  marketplace="leroy"
                                  value={productData.leroy_category_id}
                                  onChange={val => setProductData(p => ({ ...p, leroy_category_id: val }))}
                                  placeholder="Digite para buscar categoria…"
                                />
                              </div>
                            )}
                            {(selectedMarketplaces.includes('casasbahia') || selectedMarketplaces.includes('pontofrio') || selectedMarketplaces.includes('extra')) && (
                              <div>
                                <label className="block text-sm font-medium text-dark-300 mb-2">Categoria Casas Bahia / Grupo</label>
                                <CategoryAutocomplete
                                  marketplace="casas"
                                  value={productData.casasbahia_category_id}
                                  onChange={val => setProductData(p => ({ ...p, casasbahia_category_id: val }))}
                                  placeholder="Digite para buscar categoria…"
                                />
                              </div>
                            )}
                            <div>
                              <label className="block text-sm font-medium text-dark-300 mb-2">EAN / Código de Barras *</label>
                              <input type="text" value={productData.magalu_ean} onChange={e => { const v = e.target.value.replace(/\D/g, ''); setProductData(p => ({...p, ean: v, magalu_ean: v, casas_ean: v, carrefour_ean: v, leroy_ean: v})); }} className="w-full px-4 py-3 bg-dark-900 border border-dark-700 rounded-lg text-dark-100" placeholder="Obrigatório" />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-dark-300 mb-2">NCM *</label>
                              <input type="text" value={productData.magalu_ncm} onChange={e => setProductData(p => ({...p, magalu_ncm: e.target.value, casas_ncm: e.target.value}))} className="w-full px-4 py-3 bg-dark-900 border border-dark-700 rounded-lg text-dark-100" placeholder="Obrigatório" />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-dark-300 mb-2">Voltagem (Se aplicável)</label>
                              <select value={productData.casas_voltage} onChange={e => setProductData(p => ({...p, casas_voltage: e.target.value}))} className="w-full px-4 py-3 bg-dark-900 border border-dark-700 rounded-lg text-dark-100">
                                <option value="">Não se aplica</option>
                                <option value="110V">110V</option>
                                <option value="220V">220V</option>
                                <option value="BIVOLT">Bivolt</option>
                              </select>
                            </div>
                          </div>
                        </div>
                    )}

                    {(selectedMarketplaces.includes('netshoes') || selectedMarketplaces.includes('dafiti')) && (
                        <div className="p-4 bg-dark-800/50 rounded-lg border border-dark-700 mt-4">
                          <h3 className="text-lg font-semibold text-dark-100 mb-4 flex items-center gap-2">👟 Moda e Esporte (Netshoes, Dafiti)</h3>
                          <div className="grid grid-cols-3 gap-4">
                            <div>
                              <label className="text-sm font-medium text-dark-300 mb-2 block">Cor</label>
                              <input type="text" value={productData.netshoes_color} onChange={e => setProductData(p => ({...p, netshoes_color: e.target.value, dafiti_color: e.target.value}))} className="w-full px-4 py-3 bg-dark-900 border border-dark-700 rounded-lg text-dark-100" placeholder="ex: Azul" />
                            </div>
                            <div>
                              <label className="text-sm font-medium text-dark-300 mb-2 block">Tamanho</label>
                              <input type="text" value={productData.netshoes_size} onChange={e => setProductData(p => ({...p, netshoes_size: e.target.value, dafiti_size: e.target.value}))} className="w-full px-4 py-3 bg-dark-900 border border-dark-700 rounded-lg text-dark-100" placeholder="ex: M, 42" />
                            </div>
                            <div>
                              <label className="text-sm font-medium text-dark-300 mb-2 block">Gênero</label>
                              <select value={productData.netshoes_gender} onChange={e => setProductData(p => ({...p, netshoes_gender: e.target.value}))} className="w-full px-4 py-3 bg-dark-900 border border-dark-700 rounded-lg text-dark-100">
                                <option value="Unissex">Unissex</option>
                                <option value="Masculino">Masculino</option>
                                <option value="Feminino">Feminino</option>
                              </select>
                            </div>
                          </div>
                        </div>
                    )}

                  </div>
              )}

              {/* STEP 3: DIMENSÕES E IMAGENS */}
              {currentStep === 3 && (
                  <div className="space-y-6 animate-fade-in">
                    <div>
                      <h3 className="text-lg font-semibold text-dark-100 mb-4">Dimensões e Peso</h3>
                      <div className="grid grid-cols-4 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-dark-300 mb-2">Peso (kg)</label>
                          <input type="number" step="0.001" value={productData.weight} onChange={(e) => setProductData(prev => ({ ...prev, weight: e.target.value }))} className="w-full px-4 py-3 bg-dark-800 border border-dark-700 rounded-lg text-dark-100" placeholder="0.000"/>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-dark-300 mb-2">Altura (cm)</label>
                          <input type="number" step="0.1" value={productData.height} onChange={(e) => setProductData(prev => ({ ...prev, height: e.target.value }))} className="w-full px-4 py-3 bg-dark-800 border border-dark-700 rounded-lg text-dark-100" placeholder="0.0"/>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-dark-300 mb-2">Largura (cm)</label>
                          <input type="number" step="0.1" value={productData.width} onChange={(e) => setProductData(prev => ({ ...prev, width: e.target.value }))} className="w-full px-4 py-3 bg-dark-800 border border-dark-700 rounded-lg text-dark-100" placeholder="0.0"/>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-dark-300 mb-2">Comprimento (cm)</label>
                          <input type="number" step="0.1" value={productData.length} onChange={(e) => setProductData(prev => ({ ...prev, length: e.target.value }))} className="w-full px-4 py-3 bg-dark-800 border border-dark-700 rounded-lg text-dark-100" placeholder="0.0"/>
                        </div>
                      </div>
                    </div>

                    {/* === LAYOUT DE IMAGENS COM MINIATURA E ROTEAMENTO === */}
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-semibold text-dark-100">Imagens e Vídeos</h3>
                          <p className="text-xs text-dark-400 mt-1">
                            Regras ativas: Mínimo <strong>{getStrictestRules().minWidth}x{getStrictestRules().minHeight}px</strong> • Máx <strong>{getStrictestRules().maxSizeMB}MB</strong>
                          </p>
                        </div>
                        <button type="button" onClick={addImage} className="flex items-center gap-2 px-3 py-2 bg-primary-500 hover:bg-primary-600 text-dark-100 rounded-lg text-sm">
                          <Plus className="w-4 h-4" /> Adicionar Mídia
                        </button>
                      </div>

                      <div className="space-y-4">
                        {productData.images.map((imgObj, index) => {
                          const image = imgObj.url;
                          const isUploading = uploadingMap[index];
                          const hasValidImage = image && image !== 'A fazer upload...' && (image.startsWith('http') || image.startsWith('data:') || image.startsWith('/'));
                          const isVideo = hasValidImage && image.match(/\.(mp4|webm|ogg|mov|avi|mkv)$/i);

                          return (
                              <div key={index} className="flex flex-col gap-3 bg-dark-800/40 p-4 rounded-xl border border-dark-700 transition-all hover:border-dark-600">

                                <div className="flex gap-4 items-center">
                                  {/* 1. Miniatura */}
                                  <div className="relative flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border border-dark-600 bg-dark-900 flex items-center justify-center group cursor-pointer">
                                    {hasValidImage ? (
                                        isVideo ? (
                                            <div className="w-full h-full flex items-center justify-center bg-dark-800 group-hover:opacity-40 transition-opacity">
                                              <Film className="w-8 h-8 text-primary-400" />
                                            </div>
                                        ) : (
                                            <img src={resolveMediaUrl(image)} alt={`Mídia ${index + 1}`} className="w-full h-full object-cover group-hover:opacity-40 transition-opacity" />
                                        )
                                    ) : isUploading ? (
                                        <Loader className="w-6 h-6 text-primary-400 animate-spin" />
                                    ) : (
                                        <ImageIcon className="w-6 h-6 text-dark-500 group-hover:text-primary-400 transition-colors" />
                                    )}

                                    {hasValidImage && !isUploading && (
                                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                                          <Upload className="w-5 h-5 text-dark-100" />
                                        </div>
                                    )}

                                    <input
                                        type="file"
                                        accept="image/*,video/*"
                                        onChange={(e) => handleFileUpload(index, e)}
                                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                        disabled={isUploading}
                                        title={hasValidImage ? "Alterar mídia" : "Fazer upload"}
                                    />
                                  </div>

                                  {/* 2. Campo de URL ou Botão de Ação */}
                                  <div className="flex-1 flex flex-col justify-center">
                                    <div className="flex items-center justify-between mb-1">
                                      <label className={clsx("block text-xs", index === 0 ? "text-primary-400 font-bold" : "text-dark-400 font-medium")}>
                                        {index === 0 ? '⭐️ Mídia Principal' : `Mídia ${index + 1}`}
                                      </label>

                                      {/* BOTÃO PARA TORNAR PRINCIPAL */}
                                      {index !== 0 && hasValidImage && !isUploading && (
                                          <button
                                              type="button"
                                              onClick={() => setAsPrimaryImage(index)}
                                              className="text-[11px] text-dark-400 hover:text-primary-400 font-medium flex items-center gap-1 transition-colors px-2 py-0.5 rounded border border-transparent hover:border-primary-500/30 hover:bg-primary-500/10"
                                              title="Tornar esta a imagem principal do produto"
                                          >
                                            <Star className="w-3 h-3" /> Tornar Principal
                                          </button>
                                      )}
                                    </div>

                                    {hasValidImage ? (
                                        <div className="flex items-center gap-3 mt-1">
                                          <a
                                              href={image} target="_blank" rel="noopener noreferrer"
                                              className="flex items-center gap-2 px-4 py-2 bg-dark-800 hover:bg-dark-700 border border-dark-600 rounded-lg text-sm text-primary-400 transition-colors"
                                          >
                                            <ExternalLink className="w-4 h-4" />
                                            Abrir mídia em nova aba
                                          </a>
                                          <button
                                              type="button" onClick={() => updateImage(index, '')}
                                              className="text-xs text-dark-400 hover:text-dark-100 underline"
                                          >
                                            Editar URL
                                          </button>
                                        </div>
                                    ) : (
                                        <input
                                            type="url"
                                            value={image === 'A fazer upload...' ? '' : image}
                                            onChange={(e) => updateImage(index, e.target.value)}
                                            className={clsx(
                                                "w-full px-4 py-2 bg-dark-800 border rounded-lg text-sm text-dark-100 placeholder-dark-500 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors",
                                                isUploading ? "border-primary-500 animate-pulse" : "border-dark-700"
                                            )}
                                            placeholder={isUploading ? "A fazer upload..." : "Cole a URL ou clique na caixa ao lado para upload"}
                                            disabled={isUploading}
                                        />
                                    )}
                                  </div>

                                  {/* 3. Botão Remover */}
                                  {(productData.images.length > 1 || image !== '') && (
                                      <button
                                          type="button"
                                          onClick={() => {
                                            if (productData.images.length === 1) {
                                              updateImage(0, '');
                                            } else {
                                              removeImage(index);
                                            }
                                          }}
                                          className="p-3 bg-red-500/10 text-red-400 hover:bg-red-500 hover:text-dark-100 rounded-lg transition-colors flex-shrink-0 mt-5"
                                          title={productData.images.length === 1 ? "Limpar mídia" : "Remover linha"}
                                      >
                                        <Trash2 className="w-5 h-5" />
                                      </button>
                                  )}
                                </div>

                                {/* === ROTEAMENTO DA IMAGEM POR MARKETPLACE === */}
                                <div className="pt-3 mt-1 border-t border-dark-700/50">
                                  <p className="text-[10px] text-dark-400 mb-2 uppercase font-semibold tracking-wider flex items-center gap-1.5">
                                    <Upload size={12} /> Enviar esta mídia para:
                                  </p>
                                  <div className="flex flex-wrap gap-2">
                                    {selectedMarketplaces.length === 0 ? (
                                        <span className="text-xs text-red-400 italic">Selecione os marketplaces no Passo 2 primeiro.</span>
                                    ) : (
                                        selectedMarketplaces.map(mpId => {
                                          const mpInfo = MARKETPLACES.find(m => m.id === mpId);
                                          const isAllowed = imgObj.allowedMarketplaces.includes(mpId);

                                          return (
                                              <button
                                                  type="button"
                                                  key={mpId}
                                                  onClick={() => toggleImageMarketplace(index, mpId)}
                                                  className={clsx(
                                                      "px-2.5 py-1 text-xs font-medium rounded-md border transition-all flex items-center gap-1.5",
                                                      isAllowed
                                                          ? "text-dark-100 shadow-sm"
                                                          : "text-dark-400 border-dark-700 bg-dark-800 hover:bg-dark-700 opacity-60 hover:opacity-100"
                                                  )}
                                                  style={isAllowed ? { backgroundColor: mpInfo?.color, borderColor: mpInfo?.color } : {}}
                                                  title={isAllowed ? `Clique para NÃO ENVIAR ao ${mpInfo?.name}` : `Clique para ENVIAR ao ${mpInfo?.name}`}
                                              >
                                                <div className={clsx("w-1.5 h-1.5 rounded-full transition-colors", isAllowed ? "bg-white" : "bg-dark-500")} />
                                                {mpInfo?.name}
                                              </button>
                                          )
                                        })
                                    )}
                                  </div>
                                </div>

                                {/* === DIAGNÓSTICO ML === */}
                                {(() => {
                                  const diag = mlDiagnosticMap[index];
                                  if (!diag) return null;
                                  if (diag.ok && (!diag.warnings || diag.warnings.length === 0)) {
                                    return (
                                      <div className="mt-2 flex items-center gap-2 text-xs text-green-400 bg-green-500/10 border border-green-500/20 rounded-lg px-3 py-2">
                                        <span>✅</span>
                                        <span>Imagem aprovada pelo Mercado Livre</span>
                                      </div>
                                    );
                                  }
                                  return (
                                    <div className="mt-2 space-y-1">
                                      {diag.issues && diag.issues.length > 0 && (
                                        <div className="flex flex-col gap-1 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
                                          <span className="text-xs font-semibold text-red-400">❌ Problemas detectados pelo ML:</span>
                                          {diag.issues.map((issue, i) => (
                                            <span key={i} className="text-xs text-red-300">• {issue.message || issue.code}</span>
                                          ))}
                                        </div>
                                      )}
                                      {diag.warnings && diag.warnings.length > 0 && (
                                        <div className="flex flex-col gap-1 bg-yellow-500/10 border border-yellow-500/20 rounded-lg px-3 py-2">
                                          <span className="text-xs font-semibold text-yellow-400">⚠️ Avisos do ML:</span>
                                          {diag.warnings.map((w, i) => (
                                            <span key={i} className="text-xs text-yellow-300">• {w.message || w.code}</span>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })()}

                              </div>
                          )})}
                      </div>
                    </div>
                  </div>
              )}

              {/* STEP 4: ATRIBUTOS E REVISÃO */}
              {currentStep === 4 && (
                  <div className="space-y-6 animate-fade-in">
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-dark-100">Atributos Extras</h3>
                        <button type="button" onClick={addAttribute} className="flex items-center gap-2 px-3 py-2 bg-primary-500 hover:bg-primary-600 text-dark-100 rounded-lg text-sm">
                          <Plus className="w-4 h-4" /> Adicionar
                        </button>
                      </div>
                      <div className="space-y-3">
                        {productData.attributes.map((attr, index) => (
                            <div key={index} className="flex gap-3 items-start">
                              <input type="text" value={attr.name} onChange={(e) => updateAttribute(index, 'name', e.target.value)} className="flex-1 px-4 py-3 bg-dark-800 border border-dark-700 rounded-lg text-dark-100" placeholder="Nome (ex: Cor)"/>
                              <input type="text" value={attr.value} onChange={(e) => updateAttribute(index, 'value', e.target.value)} className="flex-1 px-4 py-3 bg-dark-800 border border-dark-700 rounded-lg text-dark-100" placeholder="Valor (ex: Azul)"/>
                              <button type="button" onClick={() => removeAttribute(index)} className="p-3 bg-red-500/10 text-red-400 rounded-lg"><Trash2 className="w-5 h-5" /></button>
                            </div>
                        ))}
                      </div>
                    </div>

                    <div className="p-4 bg-dark-800/50 rounded-lg border border-dark-700">
                      <h3 className="text-lg font-semibold text-dark-100 mb-4">Resumo</h3>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div><p className="text-dark-500">SKU</p><p className="text-dark-100 font-medium">{productData.sku || '-'}</p></div>
                        <div><p className="text-dark-500">Título</p><p className="text-dark-100 font-medium">{productData.title || '-'}</p></div>
                        <div><p className="text-dark-500">Preço</p><p className="text-emerald-400 font-bold">R$ {productData.basePrice || '0.00'}</p></div>
                        <div><p className="text-dark-500">Marketplaces</p><p className="text-dark-100 font-medium">{selectedMarketplaces.length} selecionado(s)</p></div>
                      </div>
                    </div>
                  </div>
              )}
            </div>

            {/* FOOTER */}
            <div className="sticky bottom-0 bg-dark-900 border-t border-dark-700 p-4 flex flex-col gap-2">
              {stepError && (
                <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-2 whitespace-pre-line">
                  {stepError}
                </div>
              )}
              <div className="flex items-center justify-between">
              <div className="flex gap-2">
                {currentStep > 1 && (
                    <button type="button" onClick={() => { setStepError(''); setCurrentStep(prev => prev - 1); }} className="px-4 py-2 bg-dark-700 hover:bg-dark-600 text-dark-100 rounded-lg transition-colors">Voltar</button>
                )}
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={onClose} className="px-4 py-2 bg-dark-700 hover:bg-dark-600 text-dark-100 rounded-lg transition-colors">Cancelar</button>
                {currentStep < 4 ? (
                    <button type="button" onClick={handleNext} className="px-6 py-2 bg-primary-500 hover:bg-primary-600 text-dark-100 rounded-lg shadow-lg transition-all">Próximo</button>
                ) : (
                    <button type="submit" disabled={loading} className="px-6 py-2 bg-emerald-500 hover:bg-emerald-600 text-dark-100 rounded-lg shadow-lg flex items-center gap-2 transition-all">
                      {loading ? 'A salvar...' : <><Upload className="w-4 h-4" /> {isCopy ? 'Criar Cópia' : product ? 'Salvar Alterações' : 'Criar Produto'}</>}
                    </button>
                )}
              </div>
              </div>
            </div>
          </form>
        </div>
      </div>
  );
}
